import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactFlow, {
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  Handle,
  Position,
  useReactFlow,
  ReactFlowProvider,
  getBezierPath,
  EdgeLabelRenderer,
  BaseEdge,
  BackgroundVariant,
  NodeResizer,
} from 'reactflow';
import 'reactflow/dist/style.css';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { Snackbar, Alert } from '@mui/material';
import { useAppPermission } from '../../hooks/useAppPermission';
import { P } from '../../constants/rbac';
import {
  getAction,
  getAutomation,
  getAutomationApp,
  getTrigger,
  postAutomationNodes,
  postAutomationLink,
  createAutomations,
  updateAutomation,
  triggerAutomationRun,
  deactivateNode,
  deactivateLink,
  updateAutomationNode,
  updateAutomationLink,
} from '../../Redux/ActionCreators/automationAction';
import { fetchJobById, fetchEvaluationVariants } from '../../Redux/ActionCreators/jobsAction';
import { getHiringStatuses } from '../../Redux/ActionCreators/settingAction';
import { useAutomationContext } from './AutomationContext';

// ─── Meta helpers ─────────────────────────────────────────────────────────────

const OP_SYM = { gte: '≥', lte: '≤', eq: '=', gt: '>', lt: '<', ne: '≠', in: 'in', not_in: 'not in' };
const OP_LABEL = { gte: '≥ at least', lte: '≤ at most', gt: '> greater than', lt: '< less than', eq: '= equals', ne: '≠ not equal', in: 'in', not_in: 'not in' };

// Condition fields come from the trigger's output_schema.condition_fields.
// "fit_label" (evaluation label) only exists for the voice call trigger; the
// rest (score, job variant) are available on every trigger.
const CONDITION_FIELD_LABEL = { score: 'Score', fit_label: 'Evaluation Label', candidate_variant_fit: 'Job Variant' };

function getConditionFieldDefs(trigger) {
  const fields = trigger?.output_schema?.condition_fields || [];
  return fields.filter(f => f.key !== 'fit_label' || trigger?.trigger_key === 'voice_call_completed');
}

// There is no "Always"/no-condition choice in the Condition dropdown, so a field
// always needs a real operator preselected — otherwise the <select> renders with
// no option matching an empty value. Prefer whatever the trigger lists first (that's
// the operator its condition_fields config actually expects); fall back by field type.
function defaultOpForField(fieldDef) {
  if (!fieldDef) return '';
  if (fieldDef.ops?.length) return fieldDef.ops[0];
  return fieldDef.value_type === 'number' ? 'gte' : 'eq';
}

// "in" / "not_in" take a list of values (e.g. matching any of several job variants).
function isArrayOp(op) {
  return op === 'in' || op === 'not_in';
}

// Reshapes backend-style enum constants (e.g. "EXCELLENT_FIT" -> "Excellent fit");
// leaves free-text values (like job variant names) untouched.
function prettifyEnumValue(value) {
  if (!value) return '';
  if (!/^[A-Z0-9_]+$/.test(String(value))) return value;
  return String(value)
    .toLowerCase()
    .split('_')
    .map((word, idx) => (idx === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(' ');
}

function isUpdateHiringStatusAction(name, action) {
  return action?.action_key === 'update_hiring_status' || (name || '').toLowerCase() === 'update hiring status';
}

// Short human-readable label of what an action node is configured to do,
// shown directly on the node so you don't have to reopen the modal to see it.
function getActionConfigSummary(name, config, action, hiringStatusesList) {
  if (!config || !Object.keys(config).length) return null;
  if (isUpdateHiringStatusAction(name, action)) {
    const status = (hiringStatusesList || []).find(s => s.id === config.hiring_status_id);
    return status ? status.name : null;
  }
  return null;
}

function nodeMeta(name, type) {
  const s = (name || '').toLowerCase();
  const sz = 18;
  const p = '#7c3aed'; // purple for triggers
  const g = '#059669'; // green for actions

  // ── Icon factories (stroke color injected) ─────────────────────────────────
  const videoIcon  = (k) => <svg width={sz} height={sz} viewBox="0 0 24 24" fill="none" stroke={k} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7" /><rect x="1" y="5" width="15" height="14" rx="2" ry="2" /></svg>;
  const phoneIcon  = (k) => <svg width={sz} height={sz} viewBox="0 0 24 24" fill="none" stroke={k} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.18 2 2 0 0 1 3.6 1h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 8.91a16 16 0 0 0 6 6l.71-.71a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" /></svg>;
  // Assessment: checkmark-in-box  (matches IconAssessment)
  const assessIcon = (k) => <svg width={sz} height={sz} viewBox="0 0 24 24" fill="none" stroke={k} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 11 12 14 22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg>;
  // Pre-screening: clipboard with lines  (matches IconPreScreening)
  const clipIcon   = (k) => <svg width={sz} height={sz} viewBox="0 0 24 24" fill="none" stroke={k} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" /><rect x="9" y="3" width="6" height="4" rx="1" /><line x1="9" y1="12" x2="15" y2="12" /><line x1="9" y1="16" x2="13" y2="16" /></svg>;
  // Person/user — for "candidate created" and generic triggers
  const personIcon = (k) => <svg width={sz} height={sz} viewBox="0 0 24 24" fill="none" stroke={k} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>;

  if (type === 'trigger') {
    let icon;
    if      (s.includes('video'))                                                   icon = videoIcon(p);
    else if (s.includes('voice') || s.includes('telephon') || s.includes('phone')) icon = phoneIcon(p);
    else if (s.includes('assessment'))                                              icon = assessIcon(p);
    else if (s.includes('screen') || s.includes('pre screen'))                     icon = clipIcon(p);
    else                                                                            icon = personIcon(p); // candidate created / other
    return { icon, color: p, bg: '#ede9fe' };
  }

  // ── Actions ────────────────────────────────────────────────────────────────
  if (s.includes('reject')) return {
    icon: <svg width={sz} height={sz} viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>,
    color: '#dc2626', bg: '#fee2e2', accent: 'linear-gradient(90deg,#dc2626,#f87171)',
  };
  if (s.includes('shortlist') || s.includes('hire') || s.includes('hired')) return {
    icon: <svg width={sz} height={sz} viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></svg>,
    color: '#d97706', bg: '#fef3c7', accent: 'linear-gradient(90deg,#d97706,#fbbf24)',
  };
  if (s.includes('video'))                                                           return { icon: videoIcon(g),  color: g, bg: '#d1fae5', accent: 'linear-gradient(90deg,#059669,#34d399)' };
  if (s.includes('telephon') || s.includes('voice') || s.includes('phone'))         return { icon: phoneIcon(g),  color: g, bg: '#d1fae5', accent: 'linear-gradient(90deg,#059669,#34d399)' };
  if (s.includes('assessment'))                                                      return { icon: assessIcon(g), color: g, bg: '#d1fae5', accent: 'linear-gradient(90deg,#059669,#34d399)' };
  if (s.includes('screen') || s.includes('pre screen'))                             return { icon: clipIcon(g),   color: g, bg: '#d1fae5', accent: 'linear-gradient(90deg,#059669,#34d399)' };
  if (s.includes('email'))                                                           return {
    icon: <svg width={sz} height={sz} viewBox="0 0 24 24" fill="none" stroke={g} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m2 7 10 6 10-6" /></svg>,
    color: g, bg: '#d1fae5', accent: 'linear-gradient(90deg,#059669,#34d399)',
  };
  return { icon: assessIcon(g), color: g, bg: '#d1fae5', accent: 'linear-gradient(90deg,#059669,#34d399)' };
}

// ─── Send-email action helpers ────────────────────────────────────────────────

const EMAIL_ALLOWED_VARS = ['candidate_name', 'job_title', 'company_name'];

function isSendEmailAction(name, action) {
  return action?.action_key === 'send_email' || (name || '').toLowerCase().includes('send email');
}

// Finds {{var}} tokens in the body that aren't in EMAIL_ALLOWED_VARS.
function getInvalidEmailVariables(text) {
  const matches = [...(text || '').matchAll(/\{\{\s*([a-zA-Z0-9_]*)\s*\}\}/g)];
  const invalid = new Set();
  matches.forEach(m => {
    if (!EMAIL_ALLOWED_VARS.includes(m[1])) invalid.add(`{{${m[1]}}}`);
  });
  return [...invalid];
}

// Saved body is wrapped as "<p>...</p>" with line breaks as "<br />"; unwrap it for editing in a plain textarea.
function stripEmailBodyTags(body) {
  const trimmed = (body || '').trim();
  const unwrapped = (trimmed.startsWith('<p>') && trimmed.endsWith('</p>')) ? trimmed.slice(3, -4) : trimmed;
  return unwrapped.replace(/<br\s*\/?>/gi, '\n');
}

// ─── Action grouping helpers ──────────────────────────────────────────────────
// Users can drag one action node on top of another (or onto an existing group)
// to bundle two or more actions into a single visual group — a bordered
// container (see GroupNode) that exposes exactly one incoming and one outgoing
// connector, no matter how many actions are inside. Grouping is entirely
// user-driven; nothing is auto-attached when an action is added.

// Actions allowed to be added to the canvas more than once (identified primarily
// by action_key from the getAction response; name-based fallback kept for safety).
const MULTI_ALLOWED_ACTION_KEYS = [
  'candidate_shortlisted',
  'candidate_hired',
  'candidate_rejected',
  'candidate_on_hold',
  'update_hiring_status',
  'send_email',
];

function isMultiAllowedAction(item) {
  if (MULTI_ALLOWED_ACTION_KEYS.includes(item?.action_key)) return true;
  const nameLower = (item?.name || '').toLowerCase();
  return nameLower.includes('shortlist') ||
    nameLower.includes('hired') ||
    nameLower.includes('hire') ||
    nameLower.includes('reject') ||
    nameLower.includes('on hold') ||
    nameLower.includes('hold') ||
    nameLower.includes('email') ||
    nameLower.includes('update hiring status');
}

// Actions must stay unique within a single group — e.g. two "Candidate
// rejected" actions in one group is never meaningful — except send-email,
// which is commonly sent more than once in sequence (screening email, then
// a follow-up). Returns true if `memberIds` contains a same-type conflict.
function findDuplicateActionInGroup(memberIds, allNodes, actionList) {
  const seenKeys = new Set();
  for (const id of memberIds) {
    const node = allNodes.find(n => n.id === id);
    if (!node) continue;
    const action = actionList.find(a => a.id === node.data?.referenceId);
    if (isSendEmailAction(node.data?.name, action)) continue;
    const key = action?.action_key || (node.data?.name || '').toLowerCase();
    if (seenKeys.has(key)) return true;
    seenKeys.add(key);
  }
  return false;
}

// Returns true if the action node has all required config filled in.
function isActionConfigured(node, actionList) {
  const name   = (node.data?.name || '').toLowerCase();
  const config = node.data?.config || {};
  const action = actionList.find(a => a.id === node.data?.referenceId);

  const isVideoCall  = name.includes('video');
  const isSendEmail  = isSendEmailAction(name, action);
  const isUpdateHiringStatus = isUpdateHiringStatusAction(name, action);
  const needsExpiry  = !isVideoCall && !isSendEmail && !isUpdateHiringStatus && !!action?.input_schema?.action_configuration?.is_required;

  if (isSendEmail) return !!(config.subject && config.body);

  if (isUpdateHiringStatus) return !!config.hiring_status_id;

  if (isVideoCall) {
    // Backend may not return video_interview_type even if previously saved;
    // treat as configured if expiry is present (fallback until backend returns the field).
    if (!config.video_interview_type) return !!(config.valid_till || config.no_of_days);
    if (config.video_interview_type === 'ai_interview') {
      return !!(config.valid_till || config.no_of_days);
    }
    return true;
  }
  if (needsExpiry) return !!(config.valid_till || config.no_of_days);
  return true;
}

// Determine if an automation has meaningful saved positions or needs auto-layout.
// Automations created by the old V1 system all have nodes at the same position.
function hasRealPositions(auto) {
  if (!auto.nodes?.length) return false;
  const xs = auto.nodes.map(n => n.position_x);
  const ys = auto.nodes.map(n => n.position_y);
  // V1-migrated automations have every node saved at the exact same
  // coordinate — that's the only case with no real position data to trust.
  // A tight-but-deliberate V2 layout (e.g. a trigger plus a small group)
  // can easily span under 250px, so any variation at all should be kept
  // instead of being discarded for the fixed grid fallback below.
  return new Set(xs).size > 1 || new Set(ys).size > 1;
}

// ─── Custom Nodes ─────────────────────────────────────────────────────────────

const TriggerNode = ({ id, data, selected }) => {
  const m = nodeMeta(data.name, 'trigger');
  return (
    <div className={`afn${selected ? ' afn--sel' : ''}`} style={{ borderColor: selected ? '#7c3aed' : undefined }}>
      <div className="afn__accent" style={{ background: 'linear-gradient(90deg,#7c3aed,#a78bfa)' }} />
      <button
        className="afn__del nodrag nopan"
        title="Remove node"
        onClick={(e) => { e.stopPropagation(); data.onDelete?.(id); }}
      >
        ×
      </button>
      <Handle type="target" position={Position.Top} id="in" className="afn__handle" style={{ background: '#7c3aed', borderColor: '#7c3aed' }} />
      <div className="afn__body">
        <div className="afn__icon" style={{ background: m.bg }}>{m.icon}</div>
        <div className="afn__meta">
          <div className="afn__label" style={{ color: '#7c3aed' }}>TRIGGER</div>
          <div className="afn__title">{data.name || 'Trigger'}</div>
        </div>
      </div>
      <Handle type="source" position={Position.Bottom} id="out" className="afn__handle" style={{ background: '#7c3aed', borderColor: '#7c3aed' }} />
    </div>
  );
};

const ActionNode = ({ id, data, selected }) => {
  const m = nodeMeta(data.name, 'action');
  const hasConfig = data.config && Object.keys(data.config).length > 0;
  const borderColor = data.hasError ? '#dc2626' : selected ? m.color : undefined;
  // Members of a group have no handles of their own — all external connections
  // attach to the single handle pair on the surrounding GroupNode instead.
  const hideHandles = !!data.hideHandles;

  return (
    <div
      className={`afn afn--clickable${selected ? ' afn--sel' : ''}${data.hasError ? ' afn--error' : ''}${hideHandles ? ' afn--in-group' : ''}`}
      style={{ borderColor }}
      onClick={() => data.onConfigure?.(id)}
    >
      <div className="afn__accent" style={{ background: m.accent || 'linear-gradient(90deg,#059669,#34d399)' }} />
      <button
        className="afn__del nodrag nopan"
        title="Remove node"
        onClick={(e) => { e.stopPropagation(); data.onDelete?.(id); }}
      >
        ×
      </button>
      {!hideHandles && <Handle type="target" position={Position.Top} id="in" className="afn__handle" style={{ background: m.color, borderColor: m.color }} />}
      <div className="afn__body">
        <div className="afn__icon" style={{ background: m.bg }}>{m.icon}</div>
        <div className="afn__meta">
          <div className="afn__label" style={{ color: m.color }}>ACTION</div>
          <div className="afn__title">{data.name || 'Action'}</div>
          {data.hasError && (
            <div style={{ fontSize: 10, color: '#dc2626', fontWeight: 600, marginTop: 2 }}>
              ⚠ Config required
            </div>
          )}
          {!data.hasError && data.configSummary && (
            <div style={{ fontSize: 10, color: m.color, fontWeight: 600, marginTop: 2 }}>
              ✓ {data.configSummary}
            </div>
          )}
          {!data.hasError && !data.configSummary && hasConfig && (
            <div style={{ fontSize: 10, color: m.color, fontWeight: 600, marginTop: 2 }}>
              ✓ Configured
            </div>
          )}
        </div>
      </div>
      {!hideHandles && <Handle type="source" position={Position.Bottom} id="out" className="afn__handle" style={{ background: m.color, borderColor: m.color }} />}
    </div>
  );
};

// Bordered container that visually bundles 2+ action nodes into one group
// (React-Flow "sub flow" style parent/child nesting). The group itself owns
// the only handles that connect to the rest of the flow — member nodes render
// on top of it via `parentNode`/`extent: 'parent'` but never connect directly.
const GroupNode = ({ id, data, selected }) => {
  const count = data.memberIds?.length || 0;
  return (
    <div className={`afg${selected ? ' afg--sel' : ''}`}>
      <NodeResizer
        nodeId={id}
        isVisible={selected}
        minWidth={GROUP_W}
        minHeight={GROUP_HEADER_H + GROUP_PAD * 2 + GROUP_MEMBER_H}
        color="#f75891"
        handleStyle={{ width: 8, height: 8, borderRadius: 2 }}
      />
      <Handle type="target" position={Position.Top} id="in" className="afg__handle" />
      <div className="afg__head">
        <span className="afg__title">Group · {count} actions</span>
        <button
          className="afg__ungroup nodrag nopan"
          title="Ungroup — split back into separate actions"
          onClick={(e) => { e.stopPropagation(); data.onUngroup?.(id); }}
        >
          ⊟ Ungroup
        </button>
      </div>
      <Handle type="source" position={Position.Bottom} id="out" className="afg__handle" />
    </div>
  );
};

// ─── Custom Edge ──────────────────────────────────────────────────────────────

const ConditionEdge = ({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, data }) => {
  const [path, lx, ly] = getBezierPath({ sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition });
  const isStage = data?.kind === 'stage';
  const isGroup = data?.kind === 'group';
  const cond = data?.condition;

  let label = 'No condition set';
  let cls = 'neutral';
  if (isStage) {
    label = 'Waits for event'; cls = 'stage';
  } else if (isGroup) {
    label = 'Group'; cls = 'group';
  } else if (cond?.op && cond.value != null) {
    const isScoreField = typeof cond.field === 'string' && cond.field.toLowerCase().includes('score');
    if (isScoreField) {
      label = `${OP_SYM[cond.op] || cond.op} ${cond.value}%`;
    } else {
      // value_label(s) carry the human-readable name(s) for id-based values (e.g. job
      // variant); plain enum values don't have one and fall back to prettifying the raw value.
      const displayValue = Array.isArray(cond.value)
        ? (cond.value_labels?.length ? cond.value_labels.join(', ') : cond.value.map(prettifyEnumValue).join(', '))
        : (cond.value_label || prettifyEnumValue(cond.value));
      label = `${OP_SYM[cond.op] || cond.op} ${displayValue}`;
    }
    cls = (cond.op === 'gte' || cond.op === 'gt') ? 'pass' : (cond.op === 'lte' || cond.op === 'lt') ? 'fail' : 'neutral';
  }
  const stroke = isStage ? '#a78bfa' : isGroup ? '#0891b2' : cls === 'pass' ? '#059669' : cls === 'fail' ? '#dc2626' : '#94a3b8';

  return (
    <>
      <BaseEdge path={path} style={{ stroke, strokeWidth: 2.5, strokeDasharray: (isStage || isGroup) ? '7 5' : undefined }} />
      <EdgeLabelRenderer>
        <div
          style={{ position: 'absolute', transform: `translate(-50%,-50%) translate(${lx}px,${ly}px)`, pointerEvents: 'all' }}
          className="nodrag nopan"
        >
          <button
            className={`afe-badge afe-badge--${cls}`}
            onClick={() => !isStage && !isGroup && data?.onOpen?.(id)}
          >
            {label}
          </button>
        </div>
      </EdgeLabelRenderer>
    </>
  );
};

// ─── Action Config Modal ──────────────────────────────────────────────────────

const ActionConfigModal = ({ nodeId, nodes, actionList, hiringStatusesList, onSave, onClose, onError }) => {
  const node    = nodes.find(n => n.id === nodeId);
  const action  = actionList.find(a => a.id === node?.data?.referenceId);
  const existing = node?.data?.config || {};

  const nameLower   = (node?.data?.name || '').toLowerCase();
  const isVideoCall = nameLower.includes('video');
  const isSendEmail = isSendEmailAction(nameLower, action);
  const isUpdateHiringStatus = isUpdateHiringStatusAction(nameLower, action);
  const needsExpiry = !isVideoCall && !isSendEmail && !isUpdateHiringStatus && !!action?.input_schema?.action_configuration?.is_required;

  const [videoType,  setVideoType]  = useState(existing.video_interview_type || '');
  const [expireType, setExpireType] = useState(existing.valid_till ? 'date' : existing.no_of_days ? 'days' : 'date');
  const [expireDate, setExpireDate] = useState(existing.valid_till ? new Date(existing.valid_till) : null);
  const [expireDays, setExpireDays] = useState(existing.no_of_days ? String(existing.no_of_days) : '');
  const [subject,    setSubject]    = useState(existing.subject || '');
  const [body,       setBody]       = useState(stripEmailBodyTags(existing.body));
  const [hiringStatusId, setHiringStatusId] = useState(existing.hiring_status_id || '');

  const showExpiry = isVideoCall ? videoType === 'ai_interview' : needsExpiry;

  const handleSave = () => {
    if (isSendEmail) {
      const trimmedSubject = subject.trim();
      const trimmedBody    = body.trim();
      if (!trimmedSubject || !trimmedBody) {
        onError?.('Subject and body are required');
        return;
      }
      const invalidVars = getInvalidEmailVariables(trimmedBody);
      if (invalidVars.length > 0) {
        onError?.(`Only {{candidate_name}}, {{job_title}}, {{company_name}} are allowed as variables. Remove: ${invalidVars.join(', ')}`);
        return;
      }
      const htmlBody = trimmedBody.replace(/\n/g, '<br />');
      onSave(nodeId, { to: 'candidate', subject: trimmedSubject, body: `<p>${htmlBody}</p>` });
      return;
    }

    if (isUpdateHiringStatus) {
      if (!hiringStatusId) {
        onError?.('Please select a hiring status');
        return;
      }
      onSave(nodeId, { hiring_status_id: hiringStatusId });
      return;
    }

    let config = {};
    if (isVideoCall) {
      config = { video_interview_type: videoType };
      if (videoType === 'ai_interview') {
        if (expireType === 'date' && expireDate) {
          config = { ...config, expire_at: expireDate.toISOString(), valid_till: expireDate.toISOString(), enable_date: true };
        } else if (expireType === 'days' && expireDays) {
          config = { ...config, enable_days: true, no_of_days: Number(expireDays) };
        }
      }
    } else {
      if (expireType === 'date' && expireDate) {
        config = { expire_at: expireDate.toISOString(), valid_till: expireDate.toISOString(), enable_date: true };
      } else if (expireType === 'days' && expireDays) {
        config = { enable_days: true, no_of_days: Number(expireDays) };
      }
    }
    onSave(nodeId, config);
  };

  return (
    <div className="af-overlay" onClick={onClose}>
      <div className={`af-modal${isSendEmail ? ' af-modal--wide' : ''}`} onClick={e => e.stopPropagation()}>
        <div className="af-modal__head">
          <h3>Configure: {node?.data?.name}</h3>
          <button className="af-modal__close" onClick={onClose}>×</button>
        </div>
        <div className="af-modal__body">
          {isVideoCall && (
            <div className="af-field">
              <label>Interview Type *</label>
              <select value={videoType} onChange={e => { setVideoType(e.target.value); setExpireDate(null); setExpireDays(''); }}>
                <option value="">Select Interview Type</option>
                <option value="hybrid">AI Assisted Interview</option>
                <option value="ai_interview">AI Lead Interview</option>
              </select>
            </div>
          )}

          {isSendEmail && (
            <>
              <div className="af-field">
                <label>Subject *</label>
                <input
                  type="text"
                  value={subject}
                  onChange={e => setSubject(e.target.value)}
                  placeholder="Enter email subject..."
                />
              </div>
              <div className="af-field">
                <div className="af-label-row">
                  <label style={{ marginBottom: 0 }}>Body *</label>
                  <span className="af-info-tip" tabIndex={0}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="16" x2="12" y2="12" />
                      <line x1="12" y1="8" x2="12.01" y2="8" />
                    </svg>
                    <span className="af-info-tip__bubble">
                      Allowed variables: {'{{candidate_name}}'}, {'{{job_title}}'}, {'{{company_name}}'}
                    </span>
                  </span>
                </div>
                <textarea
                  value={body}
                  onChange={e => setBody(e.target.value)}
                  placeholder="Enter email body... e.g. Hi {{candidate_name}}, ..."
                  rows={6}
                />
              </div>
            </>
          )}

          {isUpdateHiringStatus && (
            <div className="af-field">
              <label>Hiring Status *</label>
              <select value={hiringStatusId} onChange={e => setHiringStatusId(e.target.value)}>
                <option value="">Select Hiring Status</option>
                {(hiringStatusesList || [])
                  .filter(s => s.is_active !== false)
                  .map(s => (
                    <option key={s.id} value={s.id}>
                      {s.name} {s.type === 'default' ? '(default)' : '(custom)'}
                    </option>
                  ))}
              </select>
            </div>
          )}

          {showExpiry && (
            <div className="af-field">
              <label>Expiration</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                <div className="expire-toggle-container">
                  <button
                    className={`toggle-btn ${expireType === 'date' ? 'active' : ''}`}
                    onClick={() => { setExpireType('date'); setExpireDays(''); }}
                  >
                    Specific Date
                  </button>
                  <button
                    className={`toggle-btn ${expireType === 'days' ? 'active' : ''}`}
                    onClick={() => { setExpireType('days'); setExpireDate(null); }}
                  >
                    Days After Action
                  </button>
                </div>
                <div className="expire-input-area">
                  {expireType === 'date' && (
                    <DatePicker
                      selected={expireDate}
                      onChange={date => {
                        if (date) { const d = new Date(date); d.setHours(23, 59, 59, 999); setExpireDate(d); }
                        else setExpireDate(null);
                      }}
                      placeholderText="Select exact date..."
                      className="form-control"
                      minDate={new Date()}
                      dateFormat="MMMM d, yyyy"
                    />
                  )}
                  {expireType === 'days' && (
                    <select className="form-control" value={expireDays} onChange={e => setExpireDays(e.target.value)}>
                      <option value="">Select duration...</option>
                      {[1, 2, 3, 4, 5, 6, 7].map(day => (
                        <option key={day} value={day}>Expire {day} day{day > 1 ? 's' : ''} later</option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
            </div>
          )}

          {!isVideoCall && !isSendEmail && !isUpdateHiringStatus && !needsExpiry && (
            <p style={{ color: '#6b7280', fontSize: 14, margin: 0 }}>No additional configuration required for this action.</p>
          )}
        </div>
        <div className="af-modal__foot">
          <button className="af-btn af-btn--ghost" onClick={onClose}>Cancel</button>
          <button className="af-btn af-btn--primary" onClick={handleSave}>Save</button>
        </div>
      </div>
    </div>
  );
};

const nodeTypes = { triggerNode: TriggerNode, actionNode: ActionNode, groupNode: GroupNode };
const edgeTypes = { conditionEdge: ConditionEdge };

// ─── Sidebar ──────────────────────────────────────────────────────────────────

const Sidebar = ({ triggerList, actionList, tab, onTabChange, onAddCenter, usedTriggerIds, usedActionIds }) => {
  const items = tab === 'triggers' ? triggerList : actionList;
  const type  = tab === 'triggers' ? 'trigger' : 'action';

  const onDragStart = (e, item, disabled) => {
    if (disabled) { e.preventDefault(); return; }
    e.dataTransfer.setData('application/rf/type', type);
    e.dataTransfer.setData('application/rf/id', item.id);
    e.dataTransfer.setData('application/rf/name', item.name);
    e.dataTransfer.effectAllowed = 'move';
  };

  return (
    <aside className="af-sidebar">
      <div className="af-sidebar__tabs">
        <button className={`af-sidebar__tab${tab === 'triggers' ? ' active' : ''}`} onClick={() => onTabChange('triggers')}>Triggers</button>
        <button className={`af-sidebar__tab${tab === 'actions' ? ' active' : ''}`} onClick={() => onTabChange('actions')}>Actions</button>
      </div>
      <p className="af-sidebar__hint">Drag a block onto the canvas or click to add it.</p>
      <div className="af-sidebar__list">
        {items.map(item => {
          const m = nodeMeta(item.name, type);
          // These outcome actions are valid on multiple branches so never disable them
          const isMultiAllowed = type === 'action' && isMultiAllowedAction(item);
          const isDisabled = isMultiAllowed ? false : (
            type === 'trigger'
              ? usedTriggerIds.includes(item.id)
              : usedActionIds.includes(item.id)
          );
          return (
            <div
              key={item.id}
              className={`af-lib-item${isDisabled ? ' af-lib-item--disabled' : ''}`}
              draggable={!isDisabled}
              onDragStart={e => onDragStart(e, item, isDisabled)}
              onClick={() => !isDisabled && onAddCenter(type, item)}
              title={isDisabled ? 'Already added to the canvas' : undefined}
            >
              <div className="af-lib-icon" style={{ background: m.bg }}>{m.icon}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="af-lib-name">{item.name}</div>
                <div className="af-lib-sub">{type === 'trigger' ? 'When this happens' : 'Do this'}</div>
              </div>
              {isDisabled && <span className="af-lib-added">Added</span>}
            </div>
          );
        })}
      </div>
      <div className="af-sidebar__rules">
        <strong>Connecting</strong>
        Trigger → action: sets a condition<br />
        Action → trigger: waits for event
      </div>
    </aside>
  );
};

// ─── Condition Modal ──────────────────────────────────────────────────────────

const ConditionModal = ({ edgeId, edges, nodes, triggerList, evaluationVariants, onSave, onClose, onDelete }) => {
  const edge = edges.find(e => e.id === edgeId);
  const srcNode = nodes.find(n => n.id === edge?.source);
  const trigger = triggerList.find(t => t.id === srcNode?.data?.referenceId);

  const conditionFieldDefs = useMemo(() => getConditionFieldDefs(trigger), [trigger]);

  const existingCond = edge?.data?.condition || null;
  const existingFieldDef = existingCond
    ? conditionFieldDefs.find(f => f.field === existingCond.field)
    : null;

  const [fieldKey, setFieldKey] = useState(existingFieldDef?.key || conditionFieldDefs[0]?.key || '');
  const initialFieldDef = existingFieldDef || conditionFieldDefs[0];
  const initialOp = existingCond?.op || defaultOpForField(initialFieldDef);
  const [op, setOp] = useState(initialOp);
  const [val, setVal] = useState(existingCond?.value ?? (isArrayOp(initialOp) ? [] : ''));

  const fieldDef = conditionFieldDefs.find(f => f.key === fieldKey);

  const valueOptions = useMemo(() => {
    if (!fieldDef) return [];
    if (fieldDef.value_type === 'enum') {
      return (fieldDef.values || []).map(v => ({ value: v, label: prettifyEnumValue(v) }));
    }
    if (fieldDef.value_type === 'string' && fieldDef.value_source === 'job_evaluation_variants') {
      // Send the variant's id in the condition payload; keep the name only for display.
      return (evaluationVariants || []).map(v => ({ value: v.id, label: v.name }));
    }
    return [];
  }, [fieldDef, evaluationVariants]);

  const handleFieldChange = (key) => {
    const newFieldDef = conditionFieldDefs.find(f => f.key === key);
    const newOp = defaultOpForField(newFieldDef);
    setFieldKey(key);
    setOp(newOp);
    setVal(isArrayOp(newOp) ? [] : '');
  };

  const handleOpChange = (nextOp) => {
    setOp(nextOp);
    setVal(isArrayOp(nextOp) ? [] : '');
  };

  const hasValue = Array.isArray(val) ? val.length > 0 : val !== '';
  // A condition is always required — op is never empty (defaultOpForField guarantees
  // a real operator), so this just gates on a value being picked.
  const canSave = !!op && hasValue;

  const labelFor = (v) => valueOptions.find(o => o.value === v)?.label || prettifyEnumValue(v);

  let preview = 'Select a value to set this condition';
  if (fieldDef && op) {
    if (hasValue) {
      // valueOptions is keyed by the id sent to the backend for variant fields;
      // fall back to prettifying the raw value for plain enum fields.
      const displayVal = fieldDef.value_type === 'number'
        ? `${val}%`
        : Array.isArray(val) ? val.map(labelFor).join(', ') : labelFor(val);
      preview = `When ${(CONDITION_FIELD_LABEL[fieldKey] || fieldKey).toLowerCase()} ${OP_SYM[op] || op} ${displayVal}, run this action`;
    } else {
      preview = `Select a value for ${CONDITION_FIELD_LABEL[fieldKey] || fieldKey}`;
    }
  }

  const handleSaveClick = () => {
    if (!canSave) return;
    const isVariantField = fieldDef?.value_type === 'string' && fieldDef?.value_source === 'job_evaluation_variants';
    const value = fieldDef?.value_type === 'number' ? Number(val) : val;
    // Carried alongside the id(s) so edges/read-back UI can show the variant name(s)
    // without needing the evaluation-variants list on hand.
    const labels = isVariantField
      ? (Array.isArray(val) ? { value_labels: val.map(labelFor) } : { value_label: labelFor(val) })
      : {};
    onSave(edgeId, { field: fieldDef?.field, op, value, ...labels });
  };

  return (
    <div className="af-overlay" onClick={onClose}>
      <div className="af-modal" onClick={e => e.stopPropagation()}>
        <div className="af-modal__head">
          <h3>Condition</h3>
          <button className="af-modal__close" onClick={onClose}>×</button>
        </div>
        <div className="af-modal__body">
          {fieldDef?.value_type === 'number' && (
            <div className="af-presets">
              <button className="af-preset af-preset--pass" onClick={() => { setOp('gte'); setVal(40); }}>
                <strong>Pass</strong><span>≥ 40%</span>
              </button>
              <button className="af-preset af-preset--fail" onClick={() => { setOp('lte'); setVal(20); }}>
                <strong>Fail</strong><span>≤ 20%</span>
              </button>
            </div>
          )}

          {conditionFieldDefs.length > 1 && (
            <div className="af-field">
              <label>Field</label>
              <select value={fieldKey} onChange={e => handleFieldChange(e.target.value)}>
                {conditionFieldDefs.map(f => (
                  <option key={f.key} value={f.key}>{CONDITION_FIELD_LABEL[f.key] || f.key}</option>
                ))}
              </select>
            </div>
          )}

          <div className="af-field-row">
            <div className="af-field">
              <label>Condition</label>
              <select value={op} onChange={e => handleOpChange(e.target.value)}>
                {(fieldDef?.ops || []).map(o => (
                  <option key={o} value={o}>{OP_LABEL[o] || o}</option>
                ))}
              </select>
            </div>

            {fieldDef?.value_type === 'number' && (
              <div className="af-field">
                <label>Score %</label>
                <input
                  type="number" min="0" max="100"
                  value={val}
                  onChange={e => {
                    const v = e.target.value;
                    if (v === '') { setVal(''); return; }
                    let n = Math.floor(Number(v));
                    if (isNaN(n) || n < 0) n = 0;
                    if (n > 100) n = 100;
                    setVal(n);
                  }}
                />
              </div>
            )}

            {fieldDef && fieldDef.value_type !== 'number' && !isArrayOp(op) && (
              <div className="af-field">
                <label>Value</label>
                <select value={val} onChange={e => setVal(e.target.value)}>
                  <option value="">Select value</option>
                  {valueOptions.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {fieldDef && fieldDef.value_type !== 'number' && isArrayOp(op) && (
            <div className="af-field">
              <label>Value{Array.isArray(val) && val.length > 0 ? ` · ${val.length} selected` : ''}</label>
              <div className="af-checklist">
                {valueOptions.map(o => {
                  const checked = Array.isArray(val) && val.includes(o.value);
                  return (
                    <label key={o.value} className="af-checklist__item">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          setVal(prev => {
                            const arr = Array.isArray(prev) ? prev : [];
                            return e.target.checked ? [...arr, o.value] : arr.filter(v => v !== o.value);
                          });
                        }}
                      />
                      <span>{o.label}</span>
                    </label>
                  );
                })}
                {!valueOptions.length && <p className="af-checklist__empty">No options available</p>}
              </div>
            </div>
          )}

          <div className="af-preview">{preview}</div>
        </div>
        <div className="af-modal__foot">
          <button
            className="af-btn af-btn--ghost"
            style={{ color: '#dc2626', borderColor: '#dc2626', marginRight: 'auto' }}
            onClick={() => onDelete?.(edgeId)}
          >
            Remove link
          </button>
          <button className="af-btn af-btn--ghost" onClick={onClose}>Cancel</button>
          <button className="af-btn af-btn--primary" disabled={!canSave} onClick={handleSaveClick}>Save</button>
        </div>
      </div>
    </div>
  );
};

// ─── Inner flow component (needs useReactFlow) ────────────────────────────────

const GRID_COLS = 3;
const CELL_W    = 500;
const CELL_H    = 180;
const GRID_X0   = 50;
const GRID_Y0   = 50;

// ─── Group layout helpers ──────────────────────────────────────────────────────
// A group is a GroupNode container (see GroupNode above) that members are
// freely dragged/arranged within (side by side, stacked, however) via
// `parentNode`/`extent: 'parent'`; the box itself is user-resizable.
const GROUP_PAD        = 16;
const GROUP_HEADER_H   = 34;
const GROUP_MEMBER_W   = 196;
const GROUP_MEMBER_H   = 108;
const GROUP_MEMBER_GAP = 14;
const GROUP_W          = GROUP_MEMBER_W + GROUP_PAD * 2;

// React Flow requires a parentNode to appear before its children in the nodes
// array. Group membership changes append/reorder freely, so re-sort before
// every render-affecting update instead of tracking order by hand.
function reorderForParenting(nds) {
  const byId = new Map(nds.map(n => [n.id, n]));
  const visited = new Set();
  const result = [];
  function visit(n) {
    if (visited.has(n.id)) return;
    if (n.parentNode && byId.has(n.parentNode)) visit(byId.get(n.parentNode));
    visited.add(n.id);
    result.push(n);
  }
  nds.forEach(visit);
  return result;
}

// Grouped members can be freely dragged/arranged (side by side, stacked,
// however) within their GroupNode container, so their parent-relative
// `position` alone isn't meaningful for persistence — resolve it to an
// absolute canvas position (parent position + relative offset) instead.
function getPersistPosition(node, nodesArr) {
  if (!node?.data?.groupId) return node.position;
  const parent = nodesArr.find(n => n.id === node.data.groupId);
  if (!parent) return node.position;
  return { x: parent.position.x + node.position.x, y: parent.position.y + node.position.y };
}

// A member's on-canvas footprint, in absolute coordinates. `.afn` has no
// fixed height in CSS — a 2-line action title or a "Configured"/"Config
// required" badge line renders taller than the GROUP_MEMBER_H estimate —
// so once React Flow has measured the node (node.width/height, populated
// via its own dimension-change events) that real size is used instead of
// the estimate. Sizing a group off the estimate alone can undersize the
// box enough that `extent: 'parent'` clamps a member back inside it,
// silently moving it away from wherever the user actually placed it.
function getMemberBox(node, absPos) {
  return {
    x: absPos.x,
    y: absPos.y,
    width: node?.width || GROUP_MEMBER_W,
    height: node?.height || GROUP_MEMBER_H,
  };
}

// Smallest box (position + size) that wraps a set of members' absolute
// boxes ({x, y, width, height}), with room for the header and padding on
// every side.
function computeGroupBounds(memberBoxes) {
  const minX = Math.min(...memberBoxes.map(b => b.x));
  const minY = Math.min(...memberBoxes.map(b => b.y));
  const maxX = Math.max(...memberBoxes.map(b => b.x + (b.width || GROUP_MEMBER_W)));
  const maxY = Math.max(...memberBoxes.map(b => b.y + (b.height || GROUP_MEMBER_H)));
  return {
    position: { x: minX - GROUP_PAD, y: minY - (GROUP_HEADER_H + GROUP_PAD) },
    size: { width: Math.max(GROUP_W, (maxX - minX) + GROUP_PAD * 2), height: (maxY - minY) + GROUP_HEADER_H + GROUP_PAD * 2 },
  };
}

// True if two axis-aligned boxes ({x, y, width, height}) overlap.
function boxesOverlap(a, b) {
  return a.x < b.x + b.width && a.x + a.width > b.x &&
    a.y < b.y + b.height && a.y + a.height > b.y;
}

// A standalone (non-member) node or group's current footprint, for
// collision checks against a box about to be placed.
function getObstacleBox(node) {
  if (node.type === 'groupNode') {
    return {
      x: node.position.x, y: node.position.y,
      width: node.style?.width || GROUP_W,
      height: node.style?.height || (GROUP_HEADER_H + GROUP_PAD * 2 + GROUP_MEMBER_H),
    };
  }
  return { x: node.position.x, y: node.position.y, width: node.width || GROUP_MEMBER_W, height: node.height || GROUP_MEMBER_H };
}

// Nudges `box` straight down in fixed steps until it no longer overlaps any
// box in `obstacles`. Creating or reconstructing a group otherwise places it
// wherever its members' saved positions happen to put it, with no regard for
// other groups/nodes already occupying that space on the canvas — this is
// what made two independently-created groups render on top of each other.
function findClearPosition(box, obstacles) {
  const STEP = 40;
  let candidate = box;
  let guard = 0;
  while (guard < 300 && obstacles.some(o => boxesOverlap(candidate, o))) {
    candidate = { ...candidate, y: candidate.y + STEP };
    guard++;
  }
  return candidate;
}

// Reconstructs GroupNode containers from chains of persisted "action_group"
// marker links (see handleSave). Each maximal chain of nodes linked purely by
// action_group edges becomes one group, sized to fit wherever its members'
// saved (absolute) positions actually put them; any real (rule/stage) edge
// that pointed at a chain member is redirected to the new synthetic group id.
function applyGroupReconstruction(allNodes, allEdges, onUngroup) {
  const groupEdges = allEdges.filter(e => e.data?.kind === 'group');
  if (!groupEdges.length) return { nodes: allNodes, edges: allEdges };

  const nextOf = new Map();
  const hasIncoming = new Set();
  groupEdges.forEach(e => { nextOf.set(e.source, e.target); hasIncoming.add(e.target); });

  const nodeById = new Map(allNodes.map(n => [n.id, n]));
  const chains = [...nextOf.keys()]
    .filter(id => !hasIncoming.has(id))
    .map(headId => {
      const chain = [headId];
      let cur = headId;
      while (nextOf.has(cur)) { cur = nextOf.get(cur); chain.push(cur); }
      return chain;
    })
    .filter(chain => chain.length >= 2 && chain.every(id => nodeById.has(id)));

  const groupIdByMember = new Map();
  const newGroupNodes = [];
  // Relative offsets are computed against each group's tight (unshifted)
  // bounds, while the group itself renders at its collision-cleared
  // position — see findClearPosition above for why these can differ.
  const groupTightPosition = new Map();

  const chainMemberIds = new Set(chains.flat());
  const standaloneObstacles = allNodes
    .filter(n => !chainMemberIds.has(n.id))
    .map(getObstacleBox);
  const placedGroupObstacles = [];

  chains.forEach(chain => {
    const memberNodes = chain.map(memberId => nodeById.get(memberId));
    const headNode = memberNodes[0];
    const bounds = computeGroupBounds(memberNodes.map(n => getMemberBox(n, n.position)));
    const clearPosition = findClearPosition(
      { x: bounds.position.x, y: bounds.position.y, width: bounds.size.width, height: bounds.size.height },
      [...standaloneObstacles, ...placedGroupObstacles],
    );

    const groupId = `loaded-group-${headNode.data?.apiId ?? chain[0]}`;
    chain.forEach(memberId => groupIdByMember.set(memberId, groupId));
    groupTightPosition.set(groupId, bounds.position);
    newGroupNodes.push({
      id: groupId,
      type: 'groupNode',
      position: clearPosition,
      style: bounds.size,
      data: { memberIds: chain, onUngroup },
    });
    placedGroupObstacles.push({ x: clearPosition.x, y: clearPosition.y, width: bounds.size.width, height: bounds.size.height });
  });

  const updatedNodes = allNodes.map(n => {
    const groupId = groupIdByMember.get(n.id);
    if (!groupId) return n;
    const tightPos = groupTightPosition.get(groupId);
    return {
      ...n,
      parentNode: groupId,
      extent: 'parent',
      position: { x: n.position.x - tightPos.x, y: n.position.y - tightPos.y },
      data: { ...n.data, groupId, hideHandles: true },
    };
  });

  // Rule edges into a group post one real backend link per member (see
  // handleSave) so every action independently fires, but the canvas should
  // still show a single line into the box. Collapse same-source/same-condition
  // copies fanning into different members of one group into one visual edge,
  // keeping every member's individual link id in `fanoutApiIds` so future
  // saves know which real links to update/remove.
  const fanoutKeyToEdge = new Map();
  const updatedEdges = [];

  allEdges.forEach(e => {
    if (e.data?.kind === 'group') { updatedEdges.push(e); return; }

    const targetGroupId = groupIdByMember.get(e.target);
    if (e.data?.kind === 'rule' && targetGroupId) {
      const key = `${e.source}|${targetGroupId}|${JSON.stringify(e.data?.condition ?? null)}`;
      let collapsed = fanoutKeyToEdge.get(key);
      if (!collapsed) {
        collapsed = { ...e, target: targetGroupId, data: { ...e.data, fanoutApiIds: {} } };
        fanoutKeyToEdge.set(key, collapsed);
        updatedEdges.push(collapsed);
      }
      collapsed.data.fanoutApiIds[e.target] = e.data?.apiId;
      return;
    }

    const source = groupIdByMember.get(e.source) || e.source;
    const target = groupIdByMember.get(e.target) || e.target;
    updatedEdges.push((source === e.source && target === e.target) ? e : { ...e, source, target });
  });

  return { nodes: reorderForParenting([...updatedNodes, ...newGroupNodes]), edges: updatedEdges };
}

// Convert all automations from the API into a flat array of RF nodes and edges.
function buildFlowGraph(automationDetails, triggerList, actionList, hiringStatusesList, handleDeleteNode, setCondModalEdgeId, handleConfigOpen, handleDeleteEdge, handleUngroupAll) {
  const allNodes = [];
  const allEdges = [];

  const simple  = automationDetails.filter(a => !hasRealPositions(a));
  const complex = automationDetails.filter(a =>  hasRealPositions(a));

  const simpleRows  = Math.ceil(simple.length / GRID_COLS);
  const complexBaseY = GRID_Y0 + simpleRows * CELL_H + 80;

  // ── Simple automations: grid layout (trigger left, action right in each cell) ──
  simple.forEach((auto, idx) => {
    const col   = idx % GRID_COLS;
    const row   = Math.floor(idx / GRID_COLS);
    const baseX = GRID_X0 + col * CELL_W;
    const baseY = GRID_Y0 + row * CELL_H;
    const nodeMap = {};

    (auto.nodes || []).forEach((node) => {
      const isTrig = node.type === 'trigger';
      const meta   = (isTrig ? triggerList : actionList).find(t => t.id === node.reference_id);
      const rfId   = `loaded-${node.id}`;
      nodeMap[node.id] = rfId;

      allNodes.push({
        id: rfId,
        type: isTrig ? 'triggerNode' : 'actionNode',
        position: { x: baseX + (isTrig ? 0 : 260), y: baseY },
        data: {
          name: meta?.name || (isTrig ? 'Trigger' : 'Action'),
          apiId: node.id,
          automationId: auto.id,
          referenceId: node.reference_id,
          config: node.config || {},
          configSummary: isTrig ? null : getActionConfigSummary(meta?.name, node.config, meta, hiringStatusesList),
          onDelete: handleDeleteNode,
          onConfigure: isTrig ? undefined : handleConfigOpen,
        },
      });
    });

    (auto.links || []).forEach((link) => {
      const src = nodeMap[link.from_node_id];
      const tgt = nodeMap[link.to_node_id];
      if (!src || !tgt) return;
      const cond = link.link_condition;
      const isGroupLink = cond?.edge_kind === 'action_group';
      const isStageLink = !isGroupLink && (!cond || cond?.edge_kind === 'waits_for_event');

      allEdges.push({
        id: `loaded-link-${link.id}`,
        source: src, target: tgt,
        type: 'conditionEdge',
        data: {
          condition: (isGroupLink || isStageLink) ? null : cond,
          kind: isGroupLink ? 'group' : isStageLink ? 'stage' : 'rule',
          onOpen: (isGroupLink || isStageLink) ? undefined : setCondModalEdgeId,
          onDelete: handleDeleteEdge,
          apiId: link.id,
          automationId: auto.id,
        },
      });
    });
  });

  // ── Complex automations: use saved positions, normalized to (GRID_X0, complexBaseY) ──
  let complexXOffset = GRID_X0;

  complex.forEach((auto) => {
    const nodeMap = {};
    const xs = (auto.nodes || []).map(n => n.position_x);
    const ys = (auto.nodes || []).map(n => n.position_y);
    const minX = xs.length ? Math.min(...xs) : 0;
    const minY = ys.length ? Math.min(...ys) : 0;
    const maxX = xs.length ? Math.max(...xs) : 0;

    (auto.nodes || []).forEach((node) => {
      const isTrig = node.type === 'trigger';
      const meta   = (isTrig ? triggerList : actionList).find(t => t.id === node.reference_id);
      const rfId   = `loaded-${node.id}`;
      nodeMap[node.id] = rfId;

      allNodes.push({
        id: rfId,
        type: isTrig ? 'triggerNode' : 'actionNode',
        position: {
          x: complexXOffset + (node.position_x - minX),
          y: complexBaseY  + (node.position_y - minY),
        },
        data: {
          name: meta?.name || (isTrig ? 'Trigger' : 'Action'),
          apiId: node.id,
          automationId: auto.id,
          referenceId: node.reference_id,
          config: node.config || {},
          configSummary: isTrig ? null : getActionConfigSummary(meta?.name, node.config, meta, hiringStatusesList),
          onDelete: handleDeleteNode,
          onConfigure: isTrig ? undefined : handleConfigOpen,
        },
      });
    });

    (auto.links || []).forEach((link) => {
      const src = nodeMap[link.from_node_id];
      const tgt = nodeMap[link.to_node_id];
      if (!src || !tgt) return;
      const cond = link.link_condition;
      const isGroupLink = cond?.edge_kind === 'action_group';
      const isStageLink = !isGroupLink && (!cond || cond?.edge_kind === 'waits_for_event');

      allEdges.push({
        id: `loaded-link-${link.id}`,
        source: src, target: tgt,
        type: 'conditionEdge',
        data: {
          condition: (isGroupLink || isStageLink) ? null : cond,
          kind: isGroupLink ? 'group' : isStageLink ? 'stage' : 'rule',
          onOpen: (isGroupLink || isStageLink) ? undefined : setCondModalEdgeId,
          onDelete: handleDeleteEdge,
          apiId: link.id,
          automationId: auto.id,
        },
      });
    });

    // Next complex automation starts to the right of this one + gap
    complexXOffset += (maxX - minX) + 300;
  });

  const { nodes: groupedNodes, edges: groupedEdges } = applyGroupReconstruction(allNodes, allEdges, handleUngroupAll);
  return { allNodes: groupedNodes, allEdges: groupedEdges };
}

// The GroupNode resize handles (NodeResizer) plus React Flow's own internal
// dimension tracking can trigger the browser's "ResizeObserver loop
// completed with undelivered notifications" protection — a harmless quirk,
// but CRA's dev error overlay treats it as a fullscreen crash. Catching the
// window "error" event to suppress it doesn't work here: webpack-dev-server's
// overlay client registers its own listener before any app code runs, so it
// always sees the error first regardless of when a component adds its own
// handler. Debouncing the observer callback to the next frame instead stops
// the synchronous resize loop from ever completing, so the browser never
// fires the warning in the first place. Patched once at module load, well
// before React Flow or NodeResizer construct any observer.
if (typeof window !== 'undefined' && window.ResizeObserver && !window.ResizeObserver.__gappeoPatched) {
  const OriginalResizeObserver = window.ResizeObserver;
  class DebouncedResizeObserver extends OriginalResizeObserver {
    constructor(callback) {
      super((entries, observer) => {
        window.requestAnimationFrame(() => callback(entries, observer));
      });
    }
  }
  DebouncedResizeObserver.__gappeoPatched = true;
  window.ResizeObserver = DebouncedResizeObserver;
}

// ─────────────────────────────────────────────────────────────────────────────

const AutomationFlowInner = ({ jobId, readOnly = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { fitView, screenToFlowPosition, zoomIn, zoomOut, getIntersectingNodes } = useReactFlow();

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  // Group-management callbacks below read from this instead of the `nodes`
  // closure so they stay referentially stable across renders — otherwise,
  // since they're also dependencies of the "load automations" effect that
  // calls setNodes/setEdges, every setNodes call would change their identity
  // and re-trigger that effect, looping forever ("Maximum update depth exceeded").
  const nodesRef = useRef([]);
  nodesRef.current = nodes;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [automationIds, setAutomationIds] = useState([]);   // all loaded automation IDs
  const [automationActive, setAutomationActive] = useState(false);
  const [condModalEdgeId, setCondModalEdgeId] = useState(null);
  const [configModalNodeId, setConfigModalNodeId] = useState(null);
  const [sidebarTab, setSidebarTab] = useState('triggers');
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'error' });

  // Snapshot of the last persisted state — Reset restores to this
  const savedStateRef = useRef({ nodes: [], edges: [] });

  const ctx = useAutomationContext();
  // ctx is a new object on every JobTabsV2 render. Storing setControls in a ref
  // breaks the infinite loop: effect can call setControls without ctx being a dep.
  const setCtrlRef = useRef(null);
  setCtrlRef.current = ctx?.setControls ?? null;

  const tenantId = localStorage.getItem('tenentId');

  const automationApp     = useSelector(s => s.automation?.automationApp || null);
  const automationDetails = useSelector(s => s.automation?.automationDetails || null);
  const triggerList       = useSelector(s => s.automation?.triggerDetails || []);
  const actionList        = useSelector(s => s.automation?.actionDetails || []);
  const evaluationVariants = useSelector(s => s.jobs?.evaluationVariants || []);
  const hiringStatusesList = useSelector(s => s.setting?.hiringStatuses || []);
  const { selectedTenant } = useSelector(s => s.users);

  const automationEnabled =
    selectedTenant?.data?.subscription_plan?.configuration?.feature_flags?.automation_workflows === true ||
    selectedTenant?.data?.subscription_plan?.configuration?.feature_flags?.automation === true;

  const handleDeleteEdge = useCallback((edgeId) => {
    setEdges(eds => eds.filter(e => e.id !== edgeId));
  }, [setEdges]);

  // ── Delete a node (group-aware): deleting a whole group takes its members
  // with it; deleting one member shrinks the group, or dissolves it back to a
  // single standalone node once only one member is left. ──
  const handleDeleteNode = useCallback((nodeId) => {
    const removed = nodesRef.current.find(n => n.id === nodeId);
    if (!removed) return;

    if (removed.type === 'groupNode') {
      const memberIds = removed.data?.memberIds || [];
      const idsToRemove = new Set([nodeId, ...memberIds]);
      setNodes(nds => nds.filter(n => !idsToRemove.has(n.id)));
      setEdges(eds => eds.filter(e => !idsToRemove.has(e.source) && !idsToRemove.has(e.target)));
      return;
    }

    const groupId = removed.data?.groupId;
    if (!groupId) {
      setNodes(nds => nds.filter(n => n.id !== nodeId));
      setEdges(eds => eds.filter(e => e.source !== nodeId && e.target !== nodeId));
      return;
    }

    const groupNode = nodesRef.current.find(n => n.id === groupId);
    const remainingMemberIds = (groupNode?.data?.memberIds || []).filter(id => id !== nodeId);

    if (remainingMemberIds.length >= 2) {
      // Remaining members keep whatever position the user arranged them at —
      // no forced reflow. The box itself isn't auto-shrunk either; the user
      // can drag its NodeResizer handles down if they want it tighter.
      setNodes(nds => reorderForParenting(nds
        .filter(n => n.id !== nodeId)
        .map(n => n.id === groupId ? { ...n, data: { ...n.data, memberIds: remainingMemberIds } } : n)));
      // Rebuild the group's internal chain fresh against the closed-up order —
      // simpler and safer than patching individual chain links around the gap.
      setEdges(eds => {
        const memberSet = new Set(groupNode.data.memberIds || []);
        const withoutOldChain = eds.filter(e => !(e.data?.kind === 'group' && memberSet.has(e.source) && memberSet.has(e.target)));
        const freshChain = remainingMemberIds.slice(0, -1).map((id, i) => ({
          id: `group-link-${id}-${remainingMemberIds[i + 1]}-${Date.now()}-${i}`,
          source: id, target: remainingMemberIds[i + 1],
          type: 'conditionEdge',
          data: { kind: 'group', onDelete: handleDeleteEdge },
        }));
        return [...withoutOldChain, ...freshChain];
      });
      return;
    }

    // Only one member left — dissolve the group, the last member becomes
    // standalone at its current on-canvas spot (group position + its own
    // relative offset), wherever the user had it arranged.
    const lastId = remainingMemberIds[0];
    setNodes(nds => reorderForParenting(nds
      .filter(n => n.id !== nodeId && n.id !== groupId)
      .map(n => n.id !== lastId ? n : {
        ...n,
        parentNode: undefined,
        extent: undefined,
        position: { x: groupNode.position.x + n.position.x, y: groupNode.position.y + n.position.y },
        data: { ...n.data, groupId: undefined, hideHandles: false },
      })));
    setEdges(eds => {
      const withoutChain = eds.filter(e => e.data?.kind !== 'group' || (e.source !== groupId && e.target !== groupId && e.source !== nodeId && e.target !== nodeId));
      return lastId
        ? withoutChain.map(e => ({ ...e, source: e.source === groupId ? lastId : e.source, target: e.target === groupId ? lastId : e.target }))
        : withoutChain.filter(e => e.source !== groupId && e.target !== groupId);
    });
  }, [setNodes, setEdges, handleDeleteEdge]);

  // ── Open / save action config modal ──
  const handleConfigOpen = useCallback((nodeId) => {
    setConfigModalNodeId(nodeId);
  }, []);

  // ── Dissolve a whole group back into separate, standalone action nodes ──
  const handleUngroupAll = useCallback((groupId) => {
    const groupNode = nodesRef.current.find(n => n.id === groupId);
    if (!groupNode) return;
    const memberIds = groupNode.data?.memberIds || [];
    if (!memberIds.length) return;
    const headId = memberIds[0];
    const memberSet = new Set(memberIds);

    // Each member reappears standalone at its current on-canvas spot (group
    // position + its own relative offset) — preserves however they were arranged.
    setNodes(nds => nds
      .filter(n => n.id !== groupId)
      .map(n => {
        if (!memberSet.has(n.id)) return n;
        return {
          ...n,
          parentNode: undefined,
          extent: undefined,
          position: { x: groupNode.position.x + n.position.x, y: groupNode.position.y + n.position.y },
          data: { ...n.data, groupId: undefined, hideHandles: false },
        };
      }));

    setEdges(eds => eds
      .filter(e => !(e.data?.kind === 'group' && memberSet.has(e.source) && memberSet.has(e.target)))
      .map(e => ({
        ...e,
        source: e.source === groupId ? headId : e.source,
        target: e.target === groupId ? headId : e.target,
      })));
  }, [setNodes, setEdges]);

  // ── Bundle two standalone action nodes into a brand-new group ──
  const createGroupFromNodes = useCallback((idA, idB) => {
    const nodeA = nodesRef.current.find(n => n.id === idA);
    const nodeB = nodesRef.current.find(n => n.id === idB);
    if (!nodeA || !nodeB || nodeA.data?.groupId || nodeB.data?.groupId) return;

    if (findDuplicateActionInGroup([idA, idB], nodesRef.current, actionList)) {
      setSnack({ open: true, message: 'This action is already in the group', severity: 'error' });
      return;
    }

    const groupId = `group-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const memberIds = [idA, idB];
    // B defaults to stacking below A — a sensible starting layout the user
    // can then freely drag around (side by side, swapped, etc.) once grouped.
    const aBox = getMemberBox(nodeA, nodeA.position);
    const bAbsPos = { x: nodeA.position.x, y: nodeA.position.y + aBox.height + GROUP_MEMBER_GAP };
    const bounds = computeGroupBounds([aBox, getMemberBox(nodeB, bAbsPos)]);

    // Brand-new box — nothing else references idA/idB's old spot, so the
    // only obstacles are every other top-level node/group already on the
    // canvas (excluding group members, whose coordinates are parent-relative
    // rather than absolute and so aren't comparable here).
    const obstacles = nodesRef.current
      .filter(n => n.id !== idA && n.id !== idB && !n.parentNode)
      .map(getObstacleBox);
    const clearPosition = findClearPosition(
      { x: bounds.position.x, y: bounds.position.y, width: bounds.size.width, height: bounds.size.height },
      obstacles,
    );

    const groupNode = {
      id: groupId,
      type: 'groupNode',
      position: clearPosition,
      style: bounds.size,
      data: { memberIds, onUngroup: handleUngroupAll },
    };

    setNodes(nds => reorderForParenting([
      ...nds.filter(n => n.id !== idA && n.id !== idB),
      groupNode,
      { ...nodeA, parentNode: groupId, extent: 'parent', position: { x: nodeA.position.x - bounds.position.x, y: nodeA.position.y - bounds.position.y }, data: { ...nodeA.data, groupId, hideHandles: true } },
      { ...nodeB, parentNode: groupId, extent: 'parent', position: { x: bAbsPos.x - bounds.position.x, y: bAbsPos.y - bounds.position.y }, data: { ...nodeB.data, groupId, hideHandles: true } },
    ]));

    setEdges(eds => [
      ...eds.map(e => ({
        ...e,
        source: (e.source === idA || e.source === idB) ? groupId : e.source,
        target: (e.target === idA || e.target === idB) ? groupId : e.target,
      })),
      { id: `group-link-${idA}-${idB}`, source: idA, target: idB, type: 'conditionEdge', data: { kind: 'group', onDelete: handleDeleteEdge } },
    ]);
  }, [setNodes, setEdges, handleDeleteEdge, handleUngroupAll, actionList, setSnack]);

  // ── Add one or more standalone action nodes into an existing group ──
  const addMembersToGroup = useCallback((groupId, newMemberIds) => {
    const groupNode = nodesRef.current.find(n => n.id === groupId);
    if (!groupNode) return;
    const existingMemberIds = groupNode.data?.memberIds || [];
    const toAdd = newMemberIds.filter(id => !existingMemberIds.includes(id) && id !== groupId);
    if (!toAdd.length) return;

    if (findDuplicateActionInGroup([...existingMemberIds, ...toAdd], nodesRef.current, actionList)) {
      setSnack({ open: true, message: 'This action is already in the group', severity: 'error' });
      return;
    }

    const memberIds = [...existingMemberIds, ...toAdd];

    // Existing members keep their current arranged position; new ones default
    // to stacking below whatever currently sits lowest in the box.
    const existingAbs = existingMemberIds
      .map(id => {
        const n = nodesRef.current.find(m => m.id === id);
        if (!n) return null;
        const absPos = { x: groupNode.position.x + n.position.x, y: groupNode.position.y + n.position.y };
        return { id, ...getMemberBox(n, absPos) };
      })
      .filter(Boolean);
    let nextY = existingAbs.length
      ? Math.max(...existingAbs.map(p => p.y + p.height)) + GROUP_MEMBER_GAP
      : groupNode.position.y + GROUP_HEADER_H + GROUP_PAD;
    const newAbs = toAdd.map(id => {
      const n = nodesRef.current.find(m => m.id === id);
      const box = getMemberBox(n, { x: groupNode.position.x + GROUP_PAD, y: nextY });
      nextY += box.height + GROUP_MEMBER_GAP;
      return { id, ...box };
    });
    const bounds = computeGroupBounds([...existingAbs, ...newAbs]);
    const absById = new Map([...existingAbs, ...newAbs].map(p => [p.id, p]));

    setNodes(nds => reorderForParenting(nds.map(n => {
      if (n.id === groupId) {
        return { ...n, position: bounds.position, style: bounds.size, data: { ...n.data, memberIds } };
      }
      const abs = absById.get(n.id);
      if (!abs) return n;
      return {
        ...n,
        parentNode: groupId,
        extent: 'parent',
        position: { x: abs.x - bounds.position.x, y: abs.y - bounds.position.y },
        data: { ...n.data, groupId, hideHandles: true },
      };
    })));

    setEdges(eds => {
      const redirected = eds.map(e => ({
        ...e,
        source: toAdd.includes(e.source) ? groupId : e.source,
        target: toAdd.includes(e.target) ? groupId : e.target,
      }));
      const chainEdges = [];
      let prevId = existingMemberIds[existingMemberIds.length - 1] || null;
      toAdd.forEach(memberId => {
        if (prevId) {
          chainEdges.push({ id: `group-link-${prevId}-${memberId}`, source: prevId, target: memberId, type: 'conditionEdge', data: { kind: 'group', onDelete: handleDeleteEdge } });
        }
        prevId = memberId;
      });
      return [...redirected, ...chainEdges];
    });
  }, [setNodes, setEdges, handleDeleteEdge, actionList, setSnack]);

  // ── Merge two existing groups into one ──
  const mergeGroups = useCallback((sourceId, targetId) => {
    const sourceNode = nodesRef.current.find(n => n.id === sourceId);
    const targetNode = nodesRef.current.find(n => n.id === targetId);
    if (!sourceNode || !targetNode) return;
    const targetMemberIds = targetNode.data?.memberIds || [];
    const sourceMemberIds = sourceNode.data?.memberIds || [];
    const combinedMemberIds = [...targetMemberIds, ...sourceMemberIds];

    if (findDuplicateActionInGroup(combinedMemberIds, nodesRef.current, actionList)) {
      setSnack({ open: true, message: 'This action is already in the group', severity: 'error' });
      return;
    }

    const absOf = (ids, groupPos) => ids
      .map(id => {
        const n = nodesRef.current.find(m => m.id === id);
        if (!n) return null;
        const absPos = { x: groupPos.x + n.position.x, y: groupPos.y + n.position.y };
        return { id, ...getMemberBox(n, absPos) };
      })
      .filter(Boolean);
    const targetAbs = absOf(targetMemberIds, targetNode.position);
    const sourceAbsOriginal = absOf(sourceMemberIds, sourceNode.position);

    // Target's members keep their current arrangement; source's members move
    // in as one block below target's content, preserving their arrangement
    // relative to each other.
    const targetBottom = targetAbs.length
      ? Math.max(...targetAbs.map(p => p.y + p.height))
      : targetNode.position.y + GROUP_HEADER_H + GROUP_PAD - GROUP_MEMBER_GAP;
    const sourceTop = sourceAbsOriginal.length ? Math.min(...sourceAbsOriginal.map(p => p.y)) : 0;
    const yShift = (targetBottom + GROUP_MEMBER_GAP) - sourceTop;
    const sourceAbs = sourceAbsOriginal.map(p => ({ ...p, y: p.y + yShift }));

    const bounds = computeGroupBounds([...targetAbs, ...sourceAbs]);
    const absById = new Map([...targetAbs, ...sourceAbs].map(p => [p.id, p]));

    setNodes(nds => reorderForParenting(nds
      .filter(n => n.id !== sourceId)
      .map(n => {
        if (n.id === targetId) {
          return { ...n, position: bounds.position, style: bounds.size, data: { ...n.data, memberIds: combinedMemberIds } };
        }
        const abs = absById.get(n.id);
        if (!abs) return n;
        return {
          ...n,
          parentNode: targetId,
          extent: 'parent',
          position: { x: abs.x - bounds.position.x, y: abs.y - bounds.position.y },
          data: { ...n.data, groupId: targetId, hideHandles: true },
        };
      })));

    setEdges(eds => [
      ...eds.map(e => ({
        ...e,
        source: e.source === sourceId ? targetId : e.source,
        target: e.target === sourceId ? targetId : e.target,
      })),
      {
        id: `group-link-${targetMemberIds[targetMemberIds.length - 1]}-${sourceMemberIds[0]}`,
        source: targetMemberIds[targetMemberIds.length - 1],
        target: sourceMemberIds[0],
        type: 'conditionEdge',
        data: { kind: 'group', onDelete: handleDeleteEdge },
      },
    ]);
  }, [setNodes, setEdges, handleDeleteEdge, actionList, setSnack]);

  // ── Drag one action/group on top of another to merge them into a group ──
  const onNodeDragStop = useCallback((_evt, draggedNode) => {
    if (draggedNode.type !== 'actionNode' && draggedNode.type !== 'groupNode') return;
    // Members are draggable (for free arrangement within their box) but that's
    // just repositioning within the parent, not a merge gesture — skip it here.
    if (draggedNode.data?.groupId) return;

    // A dragged group's own members always spatially overlap it (they're
    // nested inside), so exclude them or they'd mask a genuine drop target.
    const overlaps = getIntersectingNodes(draggedNode)
      .filter(n => n.id !== draggedNode.id && n.data?.groupId !== draggedNode.id);
    const groupHit  = overlaps.find(n => n.type === 'groupNode');
    const actionHit = overlaps.find(n => n.type === 'actionNode');

    let targetGroupId = null;
    let targetActionId = null;
    if (groupHit) {
      targetGroupId = groupHit.id;
    } else if (actionHit) {
      if (actionHit.data?.groupId) targetGroupId = actionHit.data.groupId;
      else targetActionId = actionHit.id;
    }
    if (!targetGroupId && !targetActionId) return;

    if (draggedNode.type === 'groupNode') {
      if (targetGroupId && targetGroupId !== draggedNode.id) mergeGroups(draggedNode.id, targetGroupId);
      else if (targetActionId) addMembersToGroup(draggedNode.id, [targetActionId]);
      return;
    }

    if (targetGroupId) addMembersToGroup(targetGroupId, [draggedNode.id]);
    else if (targetActionId) createGroupFromNodes(targetActionId, draggedNode.id);
  }, [getIntersectingNodes, mergeGroups, addMembersToGroup, createGroupFromNodes]);

  const handleConfigSave = useCallback((nodeId, config) => {
    setNodes(nds => nds.map(n => {
      if (n.id !== nodeId) return n;
      const action = actionList.find(a => a.id === n.data?.referenceId);
      const configSummary = getActionConfigSummary(n.data?.name, config, action, hiringStatusesList);
      return { ...n, data: { ...n.data, config, configSummary, hasError: false } };
    }));
    setConfigModalNodeId(null);
  }, [setNodes, actionList, hiringStatusesList]);

  // ── Trigger IDs already on the canvas (disables them in the sidebar) ──
  const usedTriggerIds = useMemo(
    () => nodes.filter(n => n.type === 'triggerNode').map(n => n.data.referenceId).filter(Boolean),
    [nodes]
  );

  const usedActionIds = useMemo(
    () => nodes.filter(n => n.type === 'actionNode').map(n => n.data.referenceId).filter(Boolean),
    [nodes]
  );

  // Groups always render fully "expanded" as a bordered container with their
  // members nested inside (see GroupNode) — nothing to hide there. The only
  // edges kept out of view are the internal group-membership marker links.
  const visibleNodes = nodes;

  const visibleEdges = useMemo(
    () => edges.filter(e => e.data?.kind !== 'group'),
    [edges]
  );

  // ── Detect unsaved changes ──
  // Covers: new nodes/edges, removed nodes/edges, moved nodes, edited link conditions.
  // savedStateRef is a ref so not in deps, but it only mutates right before
  // setNodes/setEdges calls which retrigger this memo anyway.
  const hasChanges = useMemo(() => {
    const { nodes: sn, edges: se } = savedStateRef.current;

    // GroupNode containers are a pure UI construct with no backend entity of
    // their own (their members are the real, persistable nodes) — exclude
    // them from the "new/removed node" checks below.
    const persistableNodes = nodes.filter(n => n.type !== 'groupNode');
    const persistableSaved = sn.filter(n => n.type !== 'groupNode');

    // New items (no apiId yet)
    if (persistableNodes.some(n => !n.data?.apiId)) return true;
    if (edges.some(e => !e.data?.apiId)) return true;

    // Removed items (in snapshot but gone from canvas)
    if (persistableSaved.some(s => s.data?.apiId && !nodes.find(n => n.id === s.id))) return true;
    if (se.some(s => s.data?.apiId && !edges.find(e => e.id === s.id))) return true;

    // Position or config changed on an existing node. Grouped members persist
    // their group container's position rather than their own relative one.
    for (const node of persistableNodes) {
      if (!node.data?.apiId) continue;
      const saved = sn.find(s => s.id === node.id);
      if (saved) {
        const curPos = getPersistPosition(node, nodes);
        const savedPos = getPersistPosition(saved, sn);
        if (Math.round(curPos.x) !== Math.round(savedPos.x) ||
            Math.round(curPos.y) !== Math.round(savedPos.y)) return true;
      }
      if (saved &&
          JSON.stringify(node.data?.config ?? {}) !== JSON.stringify(saved.data?.config ?? {})) return true;
    }

    // Condition or endpoint (e.g. an existing action just got grouped) changed on an existing edge
    for (const edge of edges) {
      if (!edge.data?.apiId) continue;
      const saved = se.find(s => s.id === edge.id);
      if (saved &&
          (JSON.stringify(edge.data?.condition ?? null) !== JSON.stringify(saved.data?.condition ?? null) ||
           edge.source !== saved.source || edge.target !== saved.target))
        return true;

      // A rule edge into a group needs one real link per member — flag it if
      // the group's membership has drifted from what's already linked
      // (covers join/leave, and healing data saved before this existed).
      if (edge.data?.kind === 'rule') {
        const targetGroup = nodes.find(n => n.id === edge.target);
        if (targetGroup?.type === 'groupNode') {
          const currentMemberIds = targetGroup.data?.memberIds || [];
          const linkedMemberIds = Object.keys(edge.data?.fanoutApiIds || {});
          if (currentMemberIds.some(id => !linkedMemberIds.includes(id)) ||
              linkedMemberIds.some(id => !currentMemberIds.includes(id))) return true;
        }
      }
    }

    return false;
  }, [nodes, edges]);

  // ── Initial data fetch ──
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await dispatch(fetchJobById(jobId));
      await dispatch(getAutomationApp({ page: 1, page_size: 20 }));
      await dispatch(getTrigger(jobId));
      await dispatch(getAction(jobId));
      await dispatch(fetchEvaluationVariants(jobId));
      await dispatch(getHiringStatuses());
      setLoading(false);
    };
    init();
  }, [dispatch, jobId]);

  useEffect(() => {
    if (!automationApp?.length) return;
    dispatch(getAutomation({ job_id: jobId, tenant_id: tenantId, app_id: automationApp[0].id }));
  }, [automationApp, jobId, tenantId, dispatch]);

  // ── Convert ALL automations → React Flow graph and snapshot it ──
  useEffect(() => {
    if (!triggerList.length || !actionList.length) return;
    if (automationDetails == null) return; // not fetched yet for this job

    // Job has no automations — reset canvas instead of leaving a previous job's state on screen
    if (!automationDetails.length) {
      setAutomationIds([]);
      setAutomationActive(false);
      savedStateRef.current = { nodes: [], edges: [] };
      setNodes([]);
      setEdges([]);
      return;
    }

    // Track all IDs; active = any automation is active
    setAutomationIds(automationDetails.map(a => a.id));
    setAutomationActive(automationDetails.some(a => a.is_active));

    const { allNodes, allEdges } = buildFlowGraph(
      automationDetails,
      triggerList,
      actionList,
      hiringStatusesList,
      handleDeleteNode,
      setCondModalEdgeId,
      handleConfigOpen,
      handleDeleteEdge,
      handleUngroupAll,
    );

    savedStateRef.current = { nodes: allNodes, edges: allEdges };
    setNodes(allNodes);
    setEdges(allEdges);
    setTimeout(() => fitView({ padding: 0.1 }), 150);
  }, [automationDetails, triggerList, actionList, hiringStatusesList, handleDeleteNode, handleDeleteEdge, handleConfigOpen, handleUngroupAll, setNodes, setEdges, fitView]);

  // ── Connect nodes ── (a group behaves exactly like a single action for connection purposes)
  const onConnect = useCallback((params) => {
    const src = nodes.find(n => n.id === params.source);
    const tgt = nodes.find(n => n.id === params.target);
    if (!src || !tgt) return;

    const isActionLike = (n) => n.type === 'actionNode' || n.type === 'groupNode';
    const isRule  = src.type === 'triggerNode' && isActionLike(tgt);
    const isStage = isActionLike(src) && tgt.type === 'triggerNode';
    if (!isRule && !isStage) {
      setSnack({ open: true, message: 'Connect trigger→action or action→trigger only', severity: 'warning' });
      return;
    }

    const newId = `edge-${Date.now()}`;
    setEdges(eds => addEdge({
      ...params, id: newId, type: 'conditionEdge',
      data: { kind: isRule ? 'rule' : 'stage', condition: null, onOpen: isRule ? setCondModalEdgeId : undefined, onDelete: handleDeleteEdge },
    }, eds));

    if (isRule) setTimeout(() => setCondModalEdgeId(newId), 50);
  }, [nodes, setEdges, handleDeleteEdge]);

  // ── Drag & drop from sidebar ──
  const onDragOver = useCallback(e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }, []);

  const onDrop = useCallback(e => {
    e.preventDefault();
    const type = e.dataTransfer.getData('application/rf/type');
    const id   = e.dataTransfer.getData('application/rf/id');
    const name = e.dataTransfer.getData('application/rf/name');
    if (!type || !id) return;
    const pos = screenToFlowPosition({ x: e.clientX, y: e.clientY });

    if (type !== 'action') {
      setNodes(nds => [...nds, {
        id: `new-${Date.now()}`,
        type: 'triggerNode',
        position: pos,
        data: { name, referenceId: id, config: {}, onDelete: handleDeleteNode, onConfigure: undefined },
      }]);
      return;
    }

    const newNodeId = `new-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const newNode = {
      id: newNodeId,
      type: 'actionNode',
      position: pos,
      data: { name, referenceId: id, config: {}, onDelete: handleDeleteNode, onConfigure: handleConfigOpen },
    };

    // Dropping directly on top of an existing action (or group) forms/joins
    // a group immediately, rather than requiring a drop-then-drag-again.
    const overlaps = getIntersectingNodes({ x: pos.x, y: pos.y, width: GROUP_MEMBER_W, height: GROUP_MEMBER_H })
      .filter(n => n.type === 'actionNode' || n.type === 'groupNode');
    const groupHit  = overlaps.find(n => n.type === 'groupNode');
    const memberHit = overlaps.find(n => n.type === 'actionNode' && n.data?.groupId);
    const actionHit = overlaps.find(n => n.type === 'actionNode' && !n.data?.groupId);

    setNodes(nds => [...nds, newNode]);
    // createGroupFromNodes/addMembersToGroup look the new node up via
    // nodesRef, which only syncs on the next render — patch it here so they
    // see it immediately instead of bailing out on a stale lookup.
    nodesRef.current = [...nodesRef.current, newNode];

    if (groupHit) addMembersToGroup(groupHit.id, [newNodeId]);
    else if (memberHit) addMembersToGroup(memberHit.data.groupId, [newNodeId]);
    else if (actionHit) createGroupFromNodes(actionHit.id, newNodeId);
  }, [screenToFlowPosition, setNodes, handleDeleteNode, handleConfigOpen, getIntersectingNodes, addMembersToGroup, createGroupFromNodes]);

  // ── Add node at center via sidebar click ──
  const handleAddCenter = useCallback((type, item) => {
    const offset = nodes.length * 30;
    const pos = { x: 200 + offset, y: 200 + offset };

    if (type !== 'action') {
      setNodes(nds => [...nds, {
        id: `new-${Date.now()}`,
        type: 'triggerNode',
        position: pos,
        data: { name: item.name, referenceId: item.id, config: {}, onDelete: handleDeleteNode, onConfigure: undefined },
      }]);
      return;
    }

    setNodes(nds => [...nds, {
      id: `new-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      type: 'actionNode',
      position: pos,
      data: { name: item.name, referenceId: item.id, config: {}, onDelete: handleDeleteNode, onConfigure: handleConfigOpen },
    }]);
  }, [nodes.length, setNodes, handleDeleteNode, handleConfigOpen]);

  // ── Save condition on edge ──
  // condition is already in backend shape { field, op, value }, built by
  // ConditionModal from the trigger's condition_fields definition.
  const handleCondSave = useCallback((edgeId, condition) => {
    setEdges(eds => eds.map(e => e.id !== edgeId ? e : {
      ...e, data: { ...e.data, condition },
    }));
    setCondModalEdgeId(null);
  }, [setEdges]);

  // ── Save automation to API ──
  const handleSave = useCallback(async () => {
    const { nodes: savedNodes, edges: savedEdges } = savedStateRef.current;

    // Existing edges whose endpoint (source/target) changed — e.g. an
    // already-saved action just got dragged into a group — can't be PATCHed
    // in place (backend links don't expose a rename-endpoint op), so they're
    // recreated: the old link is deactivated and a fresh one is posted below
    // with the same condition/kind but the new endpoints.
    const endpointChangedEdges = edges.filter(e => {
      if (!e.data?.apiId) return false;
      const saved = savedEdges.find(s => s.id === e.id);
      return saved && (e.source !== saved.source || e.target !== saved.target);
    });

    // Nodes/edges that existed in the last API snapshot but are no longer on the canvas
    const removedNodes = savedNodes.filter(
      sn => sn.data?.apiId && !nodes.some(n => n.id === sn.id)
    );
    const removedEdges = [
      ...savedEdges.filter(se => se.data?.apiId && !edges.some(e => e.id === se.id)),
      ...endpointChangedEdges,
    ];

    // Nodes/edges that are brand-new (no apiId yet). GroupNode containers are a
    // pure UI construct with no backend entity — they never get an apiId, so
    // exclude them here rather than treating them as a "new node" to persist.
    const newNodes = nodes.filter(n => !n.data?.apiId && n.type !== 'groupNode');
    const newEdges = [
      ...edges.filter(e => !e.data?.apiId),
      ...endpointChangedEdges.map(e => ({ ...e, data: { ...e.data, apiId: undefined, automationId: undefined } })),
    ];

    // Existing nodes whose position moved. Grouped members persist their
    // group container's position rather than their own parent-relative one.
    const movedNodes = nodes.filter(n => {
      if (!n.data?.apiId) return false;
      const saved = savedNodes.find(s => s.id === n.id);
      if (!saved) return false;
      const curPos = getPersistPosition(n, nodes);
      const savedPos = getPersistPosition(saved, savedNodes);
      return Math.round(curPos.x) !== Math.round(savedPos.x) || Math.round(curPos.y) !== Math.round(savedPos.y);
    });

    // Existing nodes whose config changed
    const configChangedNodes = nodes.filter(n => {
      if (!n.data?.apiId) return false;
      const saved = savedNodes.find(s => s.id === n.id);
      return saved &&
        JSON.stringify(n.data?.config ?? {}) !== JSON.stringify(saved.data?.config ?? {});
    });

    // Existing edges whose condition changed (endpoint stayed the same)
    const changedEdges = edges.filter(e => {
      if (!e.data?.apiId) return false;
      const saved = savedEdges.find(s => s.id === e.id);
      if (!saved || e.source !== saved.source || e.target !== saved.target) return false;
      return JSON.stringify(e.data?.condition ?? null) !== JSON.stringify(saved.data?.condition ?? null);
    });

    // Every member of a group needs its own real, independently-firing link
    // (not just the head) — see project notes on group execution. Detect any
    // mismatch between a persisted rule-edge-into-a-group's already-linked
    // members (edge.data.fanoutApiIds) and the group's current membership,
    // so joining/leaving a group — or healing older data saved before this
    // existed — creates/removes the per-member links it needs.
    // Edges whose endpoint changed (e.g. a lone node just got merged into a
    // group) are already fully handled by the removedEdges/newEdges pair
    // above — a completely fresh link gets posted per member there, so
    // skip them here or they'd get double-posted.
    const endpointChangedIds = new Set(endpointChangedEdges.map(e => e.id));
    const fanoutDiffs = edges
      .filter(e => e.data?.kind === 'rule' && e.data?.apiId && !endpointChangedIds.has(e.id))
      .map(edge => {
        const targetGroup = nodes.find(n => n.id === edge.target);
        if (targetGroup?.type !== 'groupNode') return null;
        const currentMemberIds = targetGroup.data?.memberIds || [];
        const linkedMemberIds = Object.keys(edge.data?.fanoutApiIds || {});
        const toCreate = currentMemberIds.filter(id => !linkedMemberIds.includes(id));
        const toRemove = linkedMemberIds.filter(id => !currentMemberIds.includes(id));
        return (toCreate.length || toRemove.length) ? { edge, toCreate, toRemove } : null;
      })
      .filter(Boolean);

    const anythingChanged =
      removedNodes.length > 0 || removedEdges.length > 0 ||
      newNodes.length > 0 || newEdges.length > 0 || movedNodes.length > 0 || changedEdges.length > 0 ||
      configChangedNodes.length > 0 || fanoutDiffs.length > 0;

    if (!anythingChanged) return setSnack({ open: true, message: 'No changes to save', severity: 'info' });

    // A new action node counts as connected if it (or the group it just
    // joined) is the target of a rule edge — either a brand-new one, or an
    // existing one already linking that group to a trigger (covered by
    // fanoutDiffs above). A new trigger node counts as connected if it's the
    // *source* of a rule edge instead — rule edges always run trigger → action.
    const ruleEdges = edges.filter(e => e.data?.kind === 'rule');
    const ruleEdgeTargets = new Set(ruleEdges.map(e => e.target));
    const ruleEdgeSources = new Set(ruleEdges.map(e => e.source));
    const disconnectedNewNodes = newNodes.filter(n =>
      n.type === 'triggerNode' ? !ruleEdgeSources.has(n.id) : !ruleEdgeTargets.has(n.data?.groupId || n.id)
    );
    if (disconnectedNewNodes.length > 0)
      return setSnack({ open: true, message: 'Connect at least one trigger to an action', severity: 'error' });

    // ── Validate action node config (expiry / interview type) ──
    const newActionNodes = newNodes.filter(n => n.type === 'actionNode');
    const unconfigured  = newActionNodes.filter(n => !isActionConfigured(n, actionList));
    if (unconfigured.length > 0) {
      setNodes(nds => nds.map(n => unconfigured.some(u => u.id === n.id) ? { ...n, data: { ...n.data, hasError: true } } : n));
      return setSnack({ open: true, message: 'Configure all action nodes before saving', severity: 'error' });
    }

    try {
      setSaving(true);

      // ── Step 1: deactivate removed links first (before their nodes) ──
      // A rule edge into a group is really N real links (one per member,
      // see fanoutApiIds) collapsed into one visual edge — deactivate all of them.
      await Promise.all(
        removedEdges.flatMap(e => {
          const fanoutIds = e.data?.fanoutApiIds ? Object.values(e.data.fanoutApiIds).filter(Boolean) : null;
          return fanoutIds
            ? fanoutIds.map(linkApiId => dispatch(deactivateLink(e.data.automationId, linkApiId)))
            : [dispatch(deactivateLink(e.data.automationId, e.data.apiId))];
        })
      );

      // ── Step 2: deactivate removed nodes ──
      await Promise.all(
        removedNodes.map(n => dispatch(deactivateNode(n.data.automationId, n.data.apiId)))
      );

      // ── Step 2a: update positions of moved existing nodes (grouped members
      // persist their group container's position, see getPersistPosition) ──
      await Promise.all(
        movedNodes.map(n => {
          const pos = getPersistPosition(n, nodes);
          return dispatch(updateAutomationNode(n.data.automationId, n.data.apiId, {
            position_x: Math.round(pos.x),
            position_y: Math.round(pos.y),
          }));
        })
      );

      // ── Step 2b-config: update config of existing nodes whose config changed ──
      await Promise.all(
        configChangedNodes.map(n => dispatch(updateAutomationNode(n.data.automationId, n.data.apiId, {
          config: n.data.config || {},
        })))
      );

      // ── Step 2b: update conditions of changed existing edges ──
      // edge.data.condition is already in backend shape { field, op, value }.
      // A rule edge into a group represents N real links (fanoutApiIds) — update all of them.
      for (const edge of changedEdges) {
        const linkCondition = edge.data?.condition || null;
        const fanoutIds = edge.data?.fanoutApiIds ? Object.values(edge.data.fanoutApiIds).filter(Boolean) : null;
        if (fanoutIds) {
          await Promise.all(fanoutIds.map(linkApiId =>
            dispatch(updateAutomationLink(edge.data.automationId, linkApiId, { link_condition: linkCondition }))
          ));
        } else {
          await dispatch(updateAutomationLink(edge.data.automationId, edge.data.apiId, { link_condition: linkCondition }));
        }
      }

      // ── Step 3: reuse existing automation or create one, then add new nodes + links ──
      if (newNodes.length > 0 || newEdges.length > 0 || fanoutDiffs.length > 0) {
        let autoId;

        if (automationIds.length > 0) {
          autoId = automationIds[0];
        } else {
          const autoResp = await dispatch(createAutomations({
            name: `automation rule 1`,
            description: '',
            app_id: automationApp?.[0]?.id,
            is_template: false,
            job_id: jobId,
            tenant_id: Number(tenantId),
          }));
          if (!autoResp?.id) throw new Error('Failed to create automation');
          autoId = autoResp.id;
          // create doesn't accept is_active — activate explicitly so the
          // backend record matches the optimistic UI state set below.
          await dispatch(updateAutomation(autoId, { is_active: true }));
        }

        const nodeIdMap = {};

        // GroupNode containers have no backend entity of their own — seed the
        // map with groupId -> head member's persisted apiId (when already
        // known) so any edge pointing at the group resolves below.
        nodes.filter(n => n.type === 'groupNode').forEach(g => {
          const headNode = nodes.find(n => n.id === g.data?.memberIds?.[0]);
          if (headNode?.data?.apiId) nodeIdMap[g.id] = headNode.data.apiId;
        });

        for (const rfNode of newNodes) {
          const isTrig = rfNode.type === 'triggerNode';
          if (!rfNode.data.referenceId) continue;
          const pos = getPersistPosition(rfNode, nodes);
          const resp = await dispatch(postAutomationNodes(autoId, {
            type: isTrig ? 'trigger' : 'action',
            reference_id: rfNode.data.referenceId,
            config: rfNode.data.config || {},
            position_x: Math.round(pos.x),
            position_y: Math.round(pos.y),
          }));
          if (resp?.id) {
            nodeIdMap[rfNode.id] = resp.id;
            // If this newly-created node is the head of a group, the group's
            // synthetic id now resolves to a real node too.
            const parentGroup = rfNode.data?.groupId && nodes.find(n => n.id === rfNode.data.groupId);
            if (parentGroup?.data?.memberIds?.[0] === rfNode.id) nodeIdMap[parentGroup.id] = resp.id;
          }
        }

        for (const edge of newEdges) {
          const srcNode = nodes.find(n => n.id === edge.source);
          const tgtNode = nodes.find(n => n.id === edge.target);

          const isStageEdge = edge.data?.kind === 'stage';
          const isGroupEdge = edge.data?.kind === 'group';
          // edge.data.condition is already in backend shape { field, op, value }.
          // The "action_group" marker lets the load path (buildFlowGraph) reconstruct
          // the group container after a refresh/reload.
          const linkCondition = isStageEdge
            ? { type: 'always', label: 'Waits for event', edge_kind: 'waits_for_event' }
            : isGroupEdge
            ? { type: 'always', label: 'Group', edge_kind: 'action_group' }
            : (edge.data?.condition || null);

          // A rule edge into a group posts one real link per member (not just
          // the head) so every action independently fires — see project notes.
          if (!isStageEdge && !isGroupEdge && tgtNode?.type === 'groupNode') {
            const fromApiId = nodeIdMap[edge.source] || srcNode?.data?.apiId;
            if (!fromApiId) continue;
            const edgeAutoId = srcNode?.data?.automationId || autoId;
            for (const memberId of (tgtNode.data?.memberIds || [])) {
              const memberNode = nodes.find(n => n.id === memberId);
              const memberApiId = nodeIdMap[memberId] || memberNode?.data?.apiId;
              if (!memberApiId) continue;
              await dispatch(postAutomationLink(edgeAutoId, {
                from_node_id: fromApiId,
                to_node_id: memberApiId,
                link_condition: linkCondition,
              }));
            }
            continue;
          }

          // New nodes use nodeIdMap; existing nodes use their persisted apiId
          const fromApiId = nodeIdMap[edge.source] || srcNode?.data?.apiId;
          const toApiId   = nodeIdMap[edge.target] || tgtNode?.data?.apiId;
          if (!fromApiId || !toApiId) continue;

          // For edges between existing nodes, use their automation; otherwise the new/reused one
          const edgeAutoId = srcNode?.data?.automationId || tgtNode?.data?.automationId || autoId;
          if (!edgeAutoId) continue;

          await dispatch(postAutomationLink(edgeAutoId, {
            from_node_id: fromApiId,
            to_node_id: toApiId,
            link_condition: linkCondition,
          }));
        }

        // ── Reconcile per-member fan-out links for persisted rule edges into
        // a group whose membership changed since last save (join/leave, or
        // healing older data saved before every member got its own link). ──
        for (const { edge, toCreate, toRemove } of fanoutDiffs) {
          const srcNode = nodes.find(n => n.id === edge.source);
          const fromApiId = nodeIdMap[edge.source] || srcNode?.data?.apiId;
          const linkCondition = edge.data?.condition || null;

          for (const memberId of toCreate) {
            const memberNode = nodes.find(n => n.id === memberId);
            const memberApiId = nodeIdMap[memberId] || memberNode?.data?.apiId;
            if (!fromApiId || !memberApiId) continue;
            await dispatch(postAutomationLink(edge.data.automationId, {
              from_node_id: fromApiId,
              to_node_id: memberApiId,
              link_condition: linkCondition,
            }));
          }
          for (const memberId of toRemove) {
            const linkApiId = edge.data.fanoutApiIds?.[memberId];
            if (linkApiId) await dispatch(deactivateLink(edge.data.automationId, linkApiId));
          }
        }

        if (newNodes.length > 0) {
          await dispatch(triggerAutomationRun(autoId, 'IN_PROGRESS'));
          setAutomationIds(prev => prev.includes(autoId) ? prev : [...prev, autoId]);
          setAutomationActive(true);
        }
      }

      setSnack({ open: true, message: 'Automation saved successfully!', severity: 'success' });

      // ── Step 4: refresh canvas from API (shows deactivated items gone + new items added) ──
      await dispatch(getAutomation({ job_id: jobId, tenant_id: tenantId, app_id: automationApp?.[0]?.id }));
    } catch (err) {
      setSnack({ open: true, message: err.message || 'Failed to save automation', severity: 'error' });
    } finally {
      setSaving(false);
    }
  }, [nodes, edges, dispatch, automationApp, jobId, tenantId, automationIds, actionList, setNodes]);

  // ── Toggle all automations on/off (only flips is_active, then refreshes) ──
  const handleToggle = useCallback(async () => {
    if (!automationIds.length) return setSnack({ open: true, message: 'No automations to toggle', severity: 'warning' });
    try {
      const next = !automationActive;
      await Promise.all(
        automationIds.map(id => dispatch(updateAutomation(id, { is_active: next })))
      );
      // Optimistic update for instant UI feedback
      setAutomationActive(next);
      // Refresh from API — automationDetails useEffect will reconcile and rebuild canvas
      await dispatch(getAutomation({ job_id: jobId, tenant_id: tenantId, app_id: automationApp?.[0]?.id }));
    } catch {
      setSnack({ open: true, message: 'Failed to update automation status', severity: 'error' });
    }
  }, [automationIds, automationActive, dispatch, jobId, tenantId, automationApp]);

  // ── Reset to last saved / API state ──
  const handleReset = useCallback(() => {
    const { nodes: savedNodes, edges: savedEdges } = savedStateRef.current;
    setNodes(savedNodes);
    setEdges(savedEdges);
    setTimeout(() => { if (savedNodes.length > 0) fitView({ padding: 0.1 }); }, 100);
  }, [setNodes, setEdges, fitView]);

  const handleFit = useCallback(() => fitView({ padding: 0.1 }), [fitView]);

  // ── Register controls with JobTabsV2 header ──
  // showControls mirrors exactly when the canvas below is reachable (see the
  // "Automation is not active" early-return): either no automation has been
  // saved yet (fresh flow being built) or an existing one is active. Without
  // this, Save/Fit/Reset stayed hidden for a brand-new automation — there was
  // no backend record yet to be "active", so a first-time flow could never be
  // saved through the header controls.
  const showControls = automationIds.length === 0 || automationActive;

  // ctx is intentionally NOT in deps — it's a new object every render and
  // including it would cause an infinite setState loop. setCtrlRef.current is
  // updated synchronously each render so it always holds the current setter.
  useEffect(() => {
    setCtrlRef.current?.({
      isActive: automationActive,
      hasAutomation: automationIds.length > 0,
      showControls: readOnly ? automationIds.length > 0 || automationActive : showControls,
      hasChanges,
      saving,
      onToggle: readOnly ? undefined : handleToggle,
      onFit: handleFit,
      onReset: readOnly ? undefined : handleReset,
      onSave: readOnly ? undefined : handleSave,
    });
    return () => setCtrlRef.current?.(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [automationActive, automationIds, showControls, hasChanges, saving, handleToggle, handleFit, handleReset, handleSave, readOnly]);

  // ── Upgrade screen ──
  if (!automationEnabled) {
    return (
      <div className="af-upgrade">
        <h3>Feature Not Available</h3>
        <p>This feature is not active with your current plan.<br />Please upgrade to unlock this feature.</p>
        <button
          style={{ background: '#f75891', color: '#fff', border: 'none', borderRadius: 4, padding: '10px 24px', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}
          onClick={() => navigate('/plan')}
        >
          Upgrade Plan
        </button>
      </div>
    );
  }

  if (loading) {
    return <div className="af-loading"><div className="spinner-border" /></div>;
  }

  // ── Inactive state: automations exist but are all turned off ──
  if (automationIds.length > 0 && !automationActive) {
    return (
      <>
        <div className="af-inactive">
          <div className="af-inactive__icon">⏸</div>
          <h3 className="af-inactive__title">Automation is not active</h3>
          <p className="af-inactive__text">
            {readOnly
              ? 'Automation is turned off for this job. You have view-only access.'
              : 'Turn on automation to start processing candidates automatically based on your configured rules.'}
          </p>
          {!readOnly && (
          <div className="af-inactive__toggle-row">
            <span className="af-inactive__label">Automation</span>
            <button
              className="auto-toggle-btn"
              onClick={handleToggle}
              aria-label="Enable automation"
            />
            <span className="auto-toggle-label">Off</span>
          </div>
          )}
        </div>
        <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack(s => ({ ...s, open: false }))}>
          <Alert severity={snack.severity}>{snack.message}</Alert>
        </Snackbar>
      </>
    );
  }

  return (
    <div className={`af-layout${readOnly ? ' af-layout--readonly' : ''}`}>
      {!readOnly && (
      <Sidebar
        triggerList={triggerList}
        actionList={actionList}
        tab={sidebarTab}
        onTabChange={setSidebarTab}
        onAddCenter={handleAddCenter}
        usedTriggerIds={usedTriggerIds}
        usedActionIds={usedActionIds}
      />
      )}

      <div className="af-canvas-wrap">
        <ReactFlow
          nodes={visibleNodes}
          edges={visibleEdges}
          onNodesChange={readOnly ? undefined : onNodesChange}
          onEdgesChange={readOnly ? undefined : onEdgesChange}
          onConnect={readOnly ? undefined : onConnect}
          onNodeDragStop={readOnly ? undefined : onNodeDragStop}
          onDragOver={readOnly ? undefined : onDragOver}
          onDrop={readOnly ? undefined : onDrop}
          nodesDraggable={!readOnly}
          nodesConnectable={!readOnly}
          elementsSelectable={!readOnly}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          deleteKeyCode={readOnly ? null : "Delete"}
          fitView
          proOptions={{ hideAttribution: true }}
        >
          <Background variant={BackgroundVariant.Dots} gap={24} size={1} color="#cbd5e1" />
        </ReactFlow>
        <div className="af-zoom-controls">
          <button className="af-zoom-btn" onClick={() => zoomIn({ duration: 200 })} title="Zoom in">+</button>
          <button className="af-zoom-btn" onClick={() => zoomOut({ duration: 200 })} title="Zoom out">−</button>
        </div>
        {nodes.length === 0 && (
          <div className="af-canvas-empty">
            <div className="af-canvas-empty__icon">🔧</div>
            <p>{readOnly ? 'No automation flow configured for this job.' : 'Drag triggers and actions from the sidebar to build your flow'}</p>
          </div>
        )}
      </div>

      {!readOnly && condModalEdgeId && (
        <ConditionModal
          edgeId={condModalEdgeId}
          edges={edges}
          nodes={nodes}
          triggerList={triggerList}
          evaluationVariants={evaluationVariants}
          onSave={handleCondSave}
          onClose={() => setCondModalEdgeId(null)}
          onDelete={(edgeId) => { handleDeleteEdge(edgeId); setCondModalEdgeId(null); }}
        />
      )}

      {!readOnly && configModalNodeId && (
        <ActionConfigModal
          nodeId={configModalNodeId}
          nodes={nodes}
          actionList={actionList}
          hiringStatusesList={hiringStatusesList}
          onSave={handleConfigSave}
          onClose={() => setConfigModalNodeId(null)}
          onError={(message) => setSnack({ open: true, message, severity: 'error' })}
        />
      )}

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack(s => ({ ...s, open: false }))}>
        <Alert severity={snack.severity}>{snack.message}</Alert>
      </Snackbar>
    </div>
  );
};

// ─── Public export (wraps with ReactFlowProvider) ─────────────────────────────

const AutomationV2 = ({ jobId: propJobId }) => {
  const { jobId: routeJobId } = useParams();
  const navigate = useNavigate();
  const jobId = propJobId || routeJobId;
  const { can } = useAppPermission();
  const canViewAutomation =
    can(P.JOBS_AUTOMATION_READ) || can(P.JOBS_AUTOMATION_EDIT) || can(P.JOBS_JOB_EDIT);
  const canEditAutomation = can(P.JOBS_AUTOMATION_EDIT) || can(P.JOBS_JOB_EDIT);

  useEffect(() => {
    if (!canViewAutomation) {
      navigate(jobId ? `/jobs/${jobId}/candidates` : "/jobs", { replace: true });
    }
  }, [canViewAutomation, jobId, navigate]);

  if (!canViewAutomation) return null;

  return (
    <ReactFlowProvider>
      <AutomationFlowInner jobId={jobId} readOnly={!canEditAutomation} />
    </ReactFlowProvider>
  );
};

export default AutomationV2;